/* global React, ReactDOM, FS, FIN, AUTH, SETTINGS, ALERTS, REPORTS, EDIT, HRD, PAYROLL */
const { useState: uSh, useEffect: uEh, useMemo: uMh, useRef: uRf } = React;
const tr = (k, v) => window.t(k, v);
function Ish(name, props) { const C = window[name]; return C ? <C {...props} /> : null; }

// Retired screen ids → their new home, so old bookmarks/history entries still resolve. `dist-expenses`
// became a filter chip inside the Transaksi screen (nav item removed), so its deeplink lands there.
const SCREEN_ALIAS = { 'dist-expenses': 'dist-transactions' };
const normId = (id) => SCREEN_ALIAS[id] || id;
// Which primary nav item "owns" a hidden hub-child screen (so it stays highlighted on that child).
const HUB_OF = { ledger: 'reports', 'acct-neraca': 'reports', 'acct-labarugi': 'reports', 'acct-subscriptions': 'biaya-tetap', 'acct-accrual': 'biaya-tetap', 'acct-assets': 'biaya-tetap', moneyspots: 'entries' };

// BACKSTOP POLLING. Each REST resource is re-fetched on its SSE event; the timed polls below only
// cover a missed event. While the realtime stream is connected they therefore run every 2 minutes,
// not every 15-20s — six timers per open tab (one of them the whole cash book, up to 5000 rows)
// were a large share of the traffic that tipped the API into 429 for everyone behind one office IP.
// With the stream down they keep their original cadence, since then they are the only source.
const BACKSTOP_LIVE_MS = 120000;
const backstopLast = {};
function backstopDue(key) {
  const live = !!(window.CLOUD && typeof window.CLOUD.sseLive === 'function' && window.CLOUD.sseLive());
  if (!live) return true;
  const now = Date.now();
  if (now - (backstopLast[key] || 0) < BACKSTOP_LIVE_MS) return false;
  backstopLast[key] = now;
  return true;
}
// Config (accounts, categories, settings, fleet, roles, units … ~14 requests) changes rarely and has
// its own 'config' / 'role' events; a tab-focus resync refreshes it at most every 10 minutes.
const CONFIG_FOCUS_MIN_MS = 10 * 60 * 1000;
let configFocusAt = 0;

function navForRole(p, role) {
  // User & role administration follows the `manageUsers` CAPABILITY only — never role===.
  // Owner is configurable like anyone else; the server's lockout guard guarantees at least
  // one active admin always exists, so this can't strand the app.
  const canAdmin = !!p.manageUsers;
  const items = [];
  if (p.company) items.push({ id: 'company', label: tr('nav.company'), icon: 'IconHome', grp: 'overview' });
  if (p.company && p.reset) items.push({ id: 'projects', label: tr('nav.projects'), icon: 'IconBolt', grp: 'overview' });
  // ── FINANCE — restructured by ACCOUNTING WORKFLOW, not by screen: Ringkasan · Transaksi ·
  //    Kas & Bank · Buku Besar · Laporan · Rekonsiliasi · Tutup Buku. The last three are the
  //    double-entry engine (accounting v2). Their screens render an informative "coming soon" card
  //    until that engine's UI ships, so the information architecture is complete today with no dead
  //    links. (Kas & Bank stays a distinct item so account CRUD is never hidden — the spec folds it
  //    under "Transaksi (kas & bank)"; here it sits directly beneath Transaksi.)
  // ── FINANCE — five PRIMARY items (daily/weekly surface). The many statement/back-office screens
  //    still exist and stay navigable (deep links never 404), but are folded into two tabbed hubs
  //    (Laporan, Biaya Tetap) + a collapsible "Pengaturan Akuntansi" group. `hidden:true` = a real,
  //    navigable screen that is NOT drawn in the sidebar (it is reached via its hub's tabs). ──
  if (p.cashflow) items.push({ id: 'overview', label: tr('nav.finSummary'), icon: 'IconDashboard', grp: 'finance' });
  if (p.allEntries) items.push({ id: 'entries', label: tr('nav.finTransaksi'), icon: 'IconTx', grp: 'finance' });
  if (p.reports) items.push({ id: 'reports', label: tr('nav.reports'), icon: 'IconReport', grp: 'finance' });          // Laporan (Neraca · Laba Rugi · Jurnal · Buku Besar · Arus Kas)
  if (p.reports) items.push({ id: 'biaya-tetap', label: tr('nav.finRecurring'), icon: 'IconClock', grp: 'finance' });  // Langganan · Aset · Akrual
  if (p.reports) items.push({ id: 'acct-costing', label: tr('nav.finCosting'), icon: 'IconRefresh', grp: 'finance' });  // HPP
  // Kas & Bank folds into Transaksi (an account filter); its screen stays reachable for deep links.
  if (p.cashflow) items.push({ id: 'moneyspots', label: tr('nav.moneyspots'), icon: 'IconWallet', grp: 'finance', hidden: true });
  // Laporan hub tab-targets (deep-linkable) — hidden from the sidebar; the hub renders the right tab.
  if (p.reports) items.push({ id: 'ledger', label: tr('nav.finLedger'), grp: 'finance', hidden: true });
  if (p.reports) items.push({ id: 'acct-neraca', label: tr('nav.finBS'), grp: 'finance', hidden: true });
  if (p.reports) items.push({ id: 'acct-labarugi', label: tr('nav.finIS'), grp: 'finance', hidden: true });
  // Biaya Tetap tab-targets — hidden; the hub renders the right tab.
  if (p.reports) items.push({ id: 'acct-subscriptions', label: tr('nav.finSubs'), grp: 'finance', hidden: true });
  if (p.reports) items.push({ id: 'acct-accrual', label: tr('nav.finAccrual'), grp: 'finance', hidden: true });
  if (p.reports) items.push({ id: 'acct-assets', label: tr('nav.finAssets'), grp: 'finance', hidden: true });
  // ── SECONDARY — "Pengaturan Akuntansi" (monthly / rare / verification). Collapsed by default. ──
  if (p.reports) items.push({ id: 'acct-payables', label: tr('nav.finAP'), icon: 'IconInvoice', grp: 'acctset' });
  if (p.reports) items.push({ id: 'reconcile', label: tr('nav.finRekon'), icon: 'IconRefresh', grp: 'acctset' });
  if (p.reports) items.push({ id: 'close', label: tr('nav.finClose'), icon: 'IconLock', grp: 'acctset' });
  if (p.reports) items.push({ id: 'acct-trialbalance', label: tr('nav.finTB'), icon: 'IconReport', grp: 'acctset' });   // Neraca Saldo — a check
  if (p.reports) items.push({ id: 'acct-mapping', label: tr('nav.finMap'), icon: 'IconInvoice', grp: 'acctset' });
  if (p.sdmPayrollLihat) items.push({ id: 'acct-payroll', label: tr('nav.finPayroll'), icon: 'IconCustomers', grp: 'acctset' });
  if (p.reports && (role === 'owner' || role === 'gm')) items.push({ id: 'acct-backfill', label: tr('nav.finBackfill'), icon: 'IconRefresh', grp: 'acctset' });
  if (p.employees) items.push({ id: 'employees', label: tr('nav.employees'), icon: 'IconCustomers', grp: 'hr' });
  if (p.employees) items.push({ id: 'hrcalendar', label: tr('nav.hrcalendar'), icon: 'IconCalendar', grp: 'hr' });
  if (p.payroll) items.push({ id: 'orientation', label: tr('nav.orientation'), icon: 'IconUserCircle', grp: 'hr' });
  if (p.payroll && p.attendance) items.push({ id: 'headcount', label: tr('nav.headcount'), icon: 'IconUsersGroup', grp: 'hr' });
  if (p.payroll) items.push({ id: 'payroll', label: tr('nav.payroll'), icon: 'IconUsersGroup', grp: 'hr' });
  if (p.kasbonView) items.push({ id: 'kasbon', label: tr('nav.kasbon'), icon: 'IconWallet', grp: 'hr' });
  if (p.payroll) items.push({ id: 'thr', label: tr('nav.thr'), icon: 'IconCoinIn', grp: 'hr' });
  if (p.employees && p.attendance) items.push({ id: 'hrreport', label: tr('nav.hrreport'), icon: 'IconReport', grp: 'hr' });
  if (p.payroll && p.attendance) items.push({ id: 'hrsettings', label: tr('nav.hrsettings'), icon: 'IconSettings', grp: 'hr' });
  // Requests/Pengajuan — HR approvals (p.approvals) AND the distribusi correction/void inbox
  // (p.distribusiApprove) both live here, so the item shows for either capability.
  if (p.approvals || p.distribusiApprove || p.distribusiGantiRugiHarga) items.push({ id: 'approvals', label: tr('nav.approvals'), icon: 'IconInvoice', grp: 'admin' });
  if (p.settings) items.push({ id: 'settings', label: tr('nav.settings'), icon: 'IconSettings', grp: 'admin' });
  if (canAdmin) items.push({ id: 'users', label: tr('nav.users'), icon: 'IconUserCircle', grp: 'admin' });
  // DISTRIBUSI — a separate module. Each item shows ONLY if the user holds a capability
  // for that view (HIDDEN when they don't — never a padlock). The DISTRIBUSI group
  // therefore appears iff they hold ANY distribusi cap (p.distribusi). The server enforces
  // every cap regardless of what the sidebar shows.
  [
    { id: 'dist-dashboard', label: tr('nav.distDashboard'), icon: 'IconDashboard', caps: ['distribusiDashboard'] },
    { id: 'dist-customers', label: tr('nav.distCustomers'), icon: 'IconCustomers', caps: ['distribusiCustomers'] },
    // Pengeluaran (field expenses) now lives INSIDE the Transaksi screen (its own filter chip + inline
    // form), so it no longer has a sidebar item. distribusiExpense is added here so an expense-only
    // user still reaches the host screen; #dist-expenses deeplinks are redirected here (see normId).
    { id: 'dist-transactions', label: tr('nav.distTransactions'), icon: 'IconTx', caps: ['distribusiInput', 'distribusiKoreksi', 'distribusiExpense'] },
    { id: 'dist-deliveries', label: tr('nav.distDeliveries'), icon: 'IconTruck', caps: ['distribusiPengiriman'] },
    { id: 'dist-zones', label: tr('nav.distZones'), icon: 'IconPin', caps: ['distribusiZona'] },
    { id: 'dist-field-rules', label: tr('nav.distFieldRules'), icon: 'IconSettings', caps: ['distribusiAturanLapangan'] },
    { id: 'dist-delivery-report', label: tr('nav.distDeliveryReport'), icon: 'IconShield', caps: ['distribusiPengirimanReport'] },
    // INTERNAL loss report (Kerugian / Uang Tidak Diterima). Same owner/GM-tier cap as the action
    // that creates the rows; never customer-facing.
    { id: 'dist-loss-report', label: tr('nav.distLossReport'), title: tr('nav.distLossReportFull'), icon: 'IconWarn', caps: ['distribusiBonAdjust'] },
    { id: 'dist-adjust-report', label: tr('nav.distAdjustReport'), icon: 'IconPencil', caps: ['distribusiPenyesuaian'] },
    // GALON KITA (hitung galon) — where every gallon is, count coverage, who is out of date (Minta hitung).
    // Same cap as Stok Galon and the server's gallon-overview / count-request routes.
    { id: 'dist-galon-kita', label: tr('nav.galonKita'), icon: 'IconGrid', caps: ['gudangGalonView'] },
    // Setoran lives under DISTRIBUSI (it is field-delivery paperwork) but is otherwise UNCHANGED:
    // same screen id 'setoran' (so navigate('setoran'), the isDerivedEntry jump-back, #setoran
    // deeplink and the setoranDay/setoranMfg cash-book auto-sync all keep working), same icon, and
    // gated on the SAME p.setoran flag (caps:['setoran']). Only its menu group moved.
    { id: 'setoran', label: tr('nav.setoran'), icon: 'IconTruck', caps: ['setoran'] },
    { id: 'dist-integration', label: tr('nav.distIntegration'), icon: 'IconRefresh', caps: ['distribusiCashIntegrasi'] },
    { id: 'dist-prices', label: tr('nav.distPrices'), icon: 'IconCoinIn', caps: ['distribusiHargaMaster'] },
    { id: 'dist-audit', label: tr('nav.distAudit'), icon: 'IconShield', caps: ['distribusiAudit'] },
    // DESAIN INVOICE & NOTA — also under Pengaturan, but the owner role has no Settings screen.
    { id: 'dist-doc-design', label: tr('nav.distDocDesign'), icon: 'IconInvoice', caps: ['distribusiDokumenDesain', 'distribusiDokumenTtd'] },
  ].forEach((it) => { if (it.caps.some((c) => p[c])) items.push({ id: it.id, label: it.label, icon: it.icon, grp: 'distribusi' }); });
  // GUDANG — warehouse inventory. One screen; shown iff the user may view it.
  if (p.gudangView) items.push({ id: 'gudang', label: tr('nav.gudang'), icon: 'IconStore', grp: 'gudang' });
  // Stok Galon lives under GUDANG (it is stock, not distribution paperwork) but is still a
  // DISTRIBUSI screen in every other respect: same id/hash '#dist-gallon', same component,
  // same 'distribusiGallon' capability and fleet scope. Only the menu position moved.
  if (p.gudangGalonView) items.push({ id: 'dist-gallon', label: tr('nav.distGallon'), icon: 'IconDrop', grp: 'gudang' });
  if (p.gudangSupplier) items.push({ id: 'suppliers', label: tr('nav.suppliers'), icon: 'IconCustomers', grp: 'gudang' });
  return items;
}
const NAV_GROUPS = ['overview', 'finance', 'acctset', 'hr', 'distribusi', 'gudang', 'admin'];

// Placeholder for accounting-v2 screens (Buku Besar · Rekonsiliasi · Tutup Buku) whose backend
// (double-entry engine) is built but whose UI ships in a later stage. Keeps the workflow nav honest:
// the section exists and explains what's coming, instead of a dead tab or a 404.
// Build stamp — the RUNNING build hash (window.__AIRRO_BUILD__, the exact value the "new version"
// banner compares against /api/v1/version), plus commit + build time. Lets anyone confirm which bundle
// is live at a glance and read the hash the freshness check uses. 'dev' when served unbuilt.
function BuildStamp() {
  const build = (typeof window !== 'undefined' && window.__AIRRO_BUILD__) || 'dev';
  const commit = (typeof window !== 'undefined' && window.__AIRRO_COMMIT__) || '';
  const at = (typeof window !== 'undefined' && window.__AIRRO_BUILT_AT__) || '';
  let when = '';
  try { if (at) when = new Date(at).toLocaleString(); } catch (e) { when = at; }
  return (
    <span className="build-stamp" title={`Build ${build}${commit ? ' · commit ' + commit : ''}${when ? ' · ' + when : ''}`} style={{ fontSize: 11, color: 'var(--text-faint)', marginLeft: 10 }}>
      build {String(build).slice(0, 12)}{when ? ' · ' + when : ''}
    </span>
  );
}

function FinComingSoon({ icon, body }) {
  return (
    <div className="screen-enter fin-scope">
      <div className="card">
        <div className="fin-coming">
          <span className="fin-coming-ic">{Ish(icon, { s: 24 })}</span>
          <div className="fin-coming-t">{tr('fin.soonTitle')}</div>
          <div className="fin-coming-s">{body}</div>
        </div>
      </div>
    </div>
  );
}

// PER-UNIT MODULE TOGGLE (client mirror of BusinessUnit.enabledModules). The toggleable modules are
// the four operational nav groups; 'overview' and 'admin' (company/reports-overview + settings/users/
// approvals) are ALWAYS available so a unit can never be made unmanageable and the owner is never
// locked out. A nav item is shown only if its module is enabled for the ACTIVE unit AND the user's
// caps allow it (caps are already applied in navForRole) AND the user's unit access permits.
const MODULE_KEYS = ['finance', 'hr', 'distribusi', 'gudang'];
const NAV_MODULE_OF = { finance: 'finance', hr: 'hr', distribusi: 'distribusi', gudang: 'gudang' };   // overview/admin → always-on (undefined)
// The set of modules enabled for a unit row (enabledModules is 'all' | array of keys).
function unitModuleSet(u) {
  const em = u && u.enabledModules;
  if (!em || em === 'all') return new Set(MODULE_KEYS);
  return new Set((Array.isArray(em) ? em : MODULE_KEYS).filter((m) => MODULE_KEYS.includes(m)));
}

function FToast({ msg, onDone }) {
  uEh(() => { const t = setTimeout(onDone, 2400); return () => clearTimeout(t); }, [msg]);
  return <div className="fin-toast"><span style={{ color: '#22A7A1' }}><IconCheck s={17} /></span>{msg}</div>;
}

// Distribusi module — FOUNDATION placeholder. The real screens (Dashboard,
// Pelanggan, Transaksi, Integrasi Kas, Harga Master, Log Audit) are built next; this
// keeps navigation working and confirms access/lock state in the meantime.
function DistPlaceholder({ screen, nav }) {
  const item = (nav || []).find((n) => n.id === screen);
  return (
    <div className="screen-enter">
      <div className="card dist-ph">
        <span className="dist-ph-ic"><IconTruck s={30} /></span>
        <div className="dist-ph-t">{tr('dist.module')} · {item ? item.label : ''}</div>
        <div className="dist-ph-s">{tr('dist.phSub')}</div>
      </div>
    </div>
  );
}

function PROOFMOUNT() {
  const [proof, setProof] = uSh(null);
  uEh(() => { window.UI._viewProof = setProof; return () => { if (window.UI) window.UI._viewProof = null; }; }, []);
  return proof ? <UI.ProofViewer proof={proof} onClose={() => setProof(null)} /> : null;
}

// Global business-unit selector — STAGE 3: sets the active-unit VIEW context for the finance
// screens (cash flow, money spots, reports) and the default unit for new records. "Semua" =
// combined (exactly today's numbers). It only changes the view + the default; it never rewrites
// existing data. The choice is lifted to FApp and persisted so it survives a reload.
const ACTIVE_UNIT_KEY = 'airro_active_unit_v1';
function loadActiveUnit() { try { return localStorage.getItem(ACTIVE_UNIT_KEY) || 'all'; } catch (e) { return 'all'; } }
function UnitSelector({ units, value, onChange }) {
  if (!units || units.length < 2) return null;   // only meaningful once >1 unit exists
  return (
    <select className="unit-select" value={value} onChange={(e) => onChange(e.target.value)} title={tr('unit.label')} aria-label={tr('unit.label')}>
      <option value="all">{tr('unit.all')}</option>
      {units.filter((u) => u.active).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
    </select>
  );
}
// null/blank/unknown → "Air" (Stage 1 backfilled everyone); keeps grouping from ever dropping a row.
const unitOfRec = (r) => (r && r.businessUnitId) || 'air';

// Screens whose data carries NO businessUnitId, so the unit selector cannot filter them:
// DISTRIBUSI (transactions are scoped by fleetId, not by unit), plus Setoran/Gudang/Pemasok which
// are the same water-side operation. POLICY: they are left UNAFFECTED by the selector — and the
// indicator says so out loud, so a filtered header can never be misread as a filtered screen.
const UNIT_EXEMPT = (screen) => !!screen && (screen.indexOf('dist-') === 0 || ['setoran', 'gudang', 'suppliers'].includes(screen));
// "Unit: X" indicator — shown on every screen whenever a single unit is selected, so filtered
// numbers are never mistaken for company totals.
function UnitViewBar({ unitName, exempt }) {
  if (!unitName) return null;   // combined view → no indicator (numbers ARE company totals)
  return (
    <div className={`unit-viewbar ${exempt ? 'exempt' : ''}`}>
      <span className="unit-viewbar-tag">{tr('unit.label')}: <b>{unitName}</b></span>
      <span className="unit-viewbar-msg">{exempt ? tr('unit.exemptNote') : tr('unit.filteredNote')}</span>
    </div>
  );
}

function FApp() {
  // Auth is backend-only: never auto-login from a stale LOCAL session (left over
  // from the old localStorage build). The session is restored solely from a
  // valid backend JWT by the restore effect below; otherwise the login screen
  // shows. This guarantees CLOUD is active whenever a user is signed in, so the
  // user-management / data screens always talk to the backend.
  const [user, setUser] = uSh(null);
  // Cash book now lives in the REST per-record Entry table (like setoran), NOT the
  // /state blob — so one user deleting/editing an entry is never overwritten by
  // another client's stale whole-array push. `realEntries` holds the persisted
  // records; the setoran→cash-flow rows are DERIVED in-memory (see setoranEntries)
  // and never persisted. We seed from a local read-cache (SKIP-listed) for instant
  // paint; GET /entries is authoritative.
  const [realEntries, setRealEntries] = uSh(() => { try { const c = localStorage.getItem('airro_cashbook_cache_v1'); if (c) { const a = JSON.parse(c); if (Array.isArray(a)) return a; } } catch (e) {} return []; });
  // Categories now persist to the REST key-value store (/settings key 'airro_cats'),
  // accounts to the /accounts table — both off the /state block-mirror. Seed from
  // SKIP-listed caches for instant paint.
  const [cats, setCats] = uSh(() => { try { const c = localStorage.getItem('airro_cats_cache_v1'); if (c) { const o = JSON.parse(c); if (o && Array.isArray(o.income) && Array.isArray(o.expense)) return o; } } catch (e) {} return FS.loadCats(); });
  const [settings, setSettings] = uSh(() => FS.loadSettings());
  const [screen, setScreen] = uSh('overview');
  // Laporan / Biaya Tetap hubs: which tab is active. Seeded from the screen id so an old deep-link
  // (#acct-neraca, #ledger, #acct-assets …) opens the hub on the right tab; tab clicks are local.
  const [laporanTab, setLaporanTab] = uSh('neraca');
  const [biayaTab, setBiayaTab] = uSh('langganan');
  const [gran, setGran] = uSh('month');
  const [anchor, setAnchor] = uSh(FIN.TODAY);
  const [drawer, setDrawer] = uSh(false);
  const [toast, setToast] = uSh(null);
  const [pwModal, setPwModal] = uSh(false);   // self "Ganti Password" modal
  const [distTick, setDistTick] = uSh(0);      // bumps on a distribusi SSE event → dashboard/transaksi re-fetch
  const [deliveryAlerts, setDeliveryAlerts] = uSh([]);   // AlertBell: extra orders on today's board for the user's fleet
  const [outstandingCount, setOutstandingCount] = uSh(0);   // carry-over: undelivered stops from previous days (sidebar badge)
  const [resetAlerts, setResetAlerts] = uSh([]);         // AlertBell: pending forgot-password requests (owner/GM)
  const [changeReqAlerts, setChangeReqAlerts] = uSh([]); // AlertBell: pending distribusi correction/void requests (approvers)
  const [acctAlerts, setAcctAlerts] = uSh([]);           // AlertBell: unmapped categories / journal drift (reports users, flag on)
  const [distFormTick, setDistFormTick] = uSh(0);   // bumps when "Input Cepat" wants the Transaksi form opened
  // MODE LAPANGAN: the owner's field rules (release switch) + this phone's view/mode preference.
  const [fieldRules, setFieldRules] = uSh(null);
  const [fieldRulesTick, setFieldRulesTick] = uSh(0);   // bumped only by a 'rules' event — not every distribusi event
  const [fieldPrefs, setFieldPrefs] = uSh(() => (window.FIELDAPI ? window.FIELDAPI.loadPrefs() : {}));
  const [fieldRulesFor, setFieldRulesFor] = uSh(null);   // the user the owner's field rules were loaded for (M13)
  // Which device this is (phone / tablet = the field team, desktop = the back office). Read once per load:
  // turning a tablet or resizing a window never throws anyone out of a half-done form.
  const [fieldDevice] = uSh(() => (window.FIELDAPI && window.FIELDAPI.detectDevice ? window.FIELDAPI.detectDevice(window) : 'desktop'));
  // DISTRIBUSI HP (spec 2026-10-27): the office Distribusi screens at phone width (≤767px, live) in the
  // Liquid Glass phone layout — this user's own choice, the open Catat form.
  const hpPhone = useDhpPhone();
  const [hpSaved, setHpSaved] = uSh(null);
  const [hpEntry, setHpEntry] = uSh(null);
  const [distFleet, setDistFleet] = uSh('all');   // full-access fleet filter (GM toggle), shared across dist screens
  const [sessionExpired, setSessionExpired] = uSh(false);   // token expired → prompt re-login
  // Roles are DATA (managed via /roles). Seed FS with the cached list for instant
  // paint; the shell reloads the live list after login (reloadRoles).
  const [roles, setRolesState] = uSh(() => { try { const c = localStorage.getItem('airro_roles_cache_v1'); if (c) { const a = JSON.parse(c); if (Array.isArray(a) && a.length) { FS.setRoles(a); return a; } } } catch (e) {} return null; });
  const [editing, setEditing] = uSh(null);
  const [lang, setLang] = uSh(window.I18N.lang);
  const changeLang = (l) => { window.I18N.setLang(l); setLang(l); };
  const [hrdRates, setHrdRates] = uSh(() => HRD.loadRates());
  // Roster now lives in the REST per-record Employee table (like setoran/entries),
  // NOT the /state blob — so one user's add/edit/offboard is never overwritten by
  // another client's stale whole-array push. Seed from a SKIP-listed local cache for
  // instant paint; GET /employees?includeInactive=true is authoritative.
  const [hrdStaff, setHrdStaff] = uSh(() => { try { const c = localStorage.getItem('airro_staff_cache_v1'); if (c) { const a = JSON.parse(c); if (Array.isArray(a)) return a; } } catch (e) {} return []; });
  const [departments, setDepartments] = uSh(() => HRD.loadDepartments());
  const [positions, setPositions] = uSh(() => HRD.loadPositions());
  const [hrBudget, setHrBudget] = uSh(() => HRD.loadBudget());
  // Approvals now live in the REST per-record Approval table (like kasbon) — submit
  // + approve by different users no longer clobber each other. Seed from a
  // SKIP-listed cache; GET /approvals is authoritative.
  const [approvals, setApprovals] = uSh(() => { try { const c = localStorage.getItem('airro_approvals_cache_v1'); if (c) { const a = JSON.parse(c); if (Array.isArray(a)) return a; } } catch (e) {} return []; });
  const [accounts, setAccounts] = uSh(() => { try { const c = localStorage.getItem('airro_accounts_cache_v1'); if (c) { const a = JSON.parse(c); if (Array.isArray(a) && a.length) return a; } } catch (e) {} return FS.loadAccts(); });
  // Business units (Stage 3): the dictionary + the active-unit VIEW context. `activeUnit`
  // filters the finance screens and defaults new records; 'all' = combined (today's numbers).
  const [businessUnits, setBusinessUnits] = uSh(() => { try { const c = localStorage.getItem('airro_bunits_cache_v1'); if (c) { const a = JSON.parse(c); if (Array.isArray(a) && a.length) return a; } } catch (e) {} return [{ id: 'air', name: 'Air', code: 'AIR', active: true }]; });
  const [activeUnit, setActiveUnitState] = uSh(loadActiveUnit);
  const setActiveUnit = (u) => { setActiveUnitState(u); try { localStorage.setItem(ACTIVE_UNIT_KEY, u); } catch (e) {} };
  // If the active unit was deactivated/removed, fall back to combined so nothing looks empty.
  uEh(() => { if (activeUnit !== 'all' && businessUnits.length && !businessUnits.some((u) => u.id === activeUnit && u.active !== false)) setActiveUnit('all'); }, [businessUnits]);
  const unitDefaultForNew = () => {
    if (activeUnit !== 'all') return activeUnit;
    // A user scoped away from "Air" must not default new records into a unit they can't see.
    // (unitScope is declared below but this closure only runs from later event handlers.)
    if (unitScope && !unitScope.includes('air')) return unitScope[0];
    return 'air';
  };
  // Setoran now lives in the REST table (per-record), NOT the /state blob — so
  // concurrent edits by different users never overwrite each other. We seed from a
  // local read-cache (SKIP-listed, not mirrored) just for instant paint on reload;
  // the authoritative list is (re)loaded from GET /setoran.
  const [setoran, setSetoran] = uSh(() => { try { const c = localStorage.getItem('airro_setoran_cache_v1'); if (c) { const a = JSON.parse(c); if (Array.isArray(a)) return a; } } catch (e) {} return []; });
  const [fleet, setFleet] = uSh(() => { try { const c = localStorage.getItem('airro_fleet_cache_v1'); if (c) { const a = JSON.parse(c); if (Array.isArray(a) && a.length) return a; } } catch (e) {} return FS.loadFleet(); });
  const [transfers, setTransfers] = uSh(() => { try { const c = localStorage.getItem('airro_transfers_cache_v1'); if (c) { const a = JSON.parse(c); if (Array.isArray(a)) return a; } } catch (e) {} return FS.loadTransfers(); });
  const [projects, setProjects] = uSh(() => CO.loadProjects());
  // Kasbon now lives in the REST per-record Cashbon table (like setoran/entries/
  // staff), NOT the /state blob — submit/approve by different users no longer clobber
  // each other. Seed from a SKIP-listed cache; GET /cashbon is authoritative.
  const [cashbons, setCashbons] = uSh(() => { try { const c = localStorage.getItem('airro_cashbon_cache_v1'); if (c) { const a = JSON.parse(c); if (Array.isArray(a)) return a; } } catch (e) {} return []; });
  // HR calendar events now live in the REST CalendarEvent table (per-record). Seed
  // from a SKIP-listed cache; GET /calendar is authoritative.
  const [calEvents, setCalEvents] = uSh(() => { try { const c = localStorage.getItem('airro_calendar_cache_v1'); if (c) { const a = JSON.parse(c); if (Array.isArray(a)) return a; } } catch (e) {} return []; });
  const [users, setUsers] = uSh(() => FS.loadUsers());
  const [empDetail, setEmpDetail] = uSh(null);
  const [syncStatus, setSyncStatus] = uSh('saved');   // 'saving' | 'saved' | 'error' from the cloud adapter
  const [syncTick, setSyncTick] = uSh(0);             // bumps on every applied remote change → forces on-demand readers (attendance / orientation) to re-read
  const [navOpen, setNavOpen] = uSh(() => { try { return JSON.parse(localStorage.getItem('airro_navopen_v1')) || {}; } catch (e) { return {}; } });

  // settings/rates/budget/departments/projects persist to REST /settings keys (see
  // the config slices below) — no longer mirrored to /state.
  uEh(() => { FS.saveUsers(users); }, [users]);   // fleet/transfers are REST-loaded (see config slices below)

  // Per-user permission override (set by the GM) takes precedence over the role defaults.
  const p = FS.normKasbon((user && user.permissions) ? user.permissions : FS.perms(user ? user.role : 'cashier'));
  // MODE LAPANGAN — which view the Pengiriman screen opens (old board or the new phone UI) and in which
  // mode; decided by one pure rule (FIELDAPI.prefState) from the caps, the owner's release switch and
  // this phone's preference. No demo cap and not released → the old board exactly as before. A desktop
  // opens the office app (it may still open the field view from Pengiriman).
  const fieldPref = window.FIELDAPI ? window.FIELDAPI.prefState({ perms: p, rules: fieldRules, prefs: fieldPrefs, role: user && user.role, device: fieldDevice }) : { eligible: false, ui: 'old', mode: 'latihan' };
  const setFieldPref = (next) => { const merged = Object.assign({}, fieldPrefs, next); setFieldPrefs(merged); if (window.FIELDAPI) window.FIELDAPI.savePrefs(merged); };
  uEh(() => {
    if (!user || !(p.distribusiPengiriman || p.distribusi) || !window.API || !window.API.distribusi || !window.API.distribusi.fieldRules) { setFieldRules(null); setFieldRulesFor(user ? user.id : null); return undefined; }
    let live = true;
    const done = () => { if (live) setFieldRulesFor(user.id); };
    const t = setTimeout(done, 6000);   // a slow server never keeps the screen blank longer than this
    window.API.distribusi.fieldRules.get().then((r) => { if (live) setFieldRules((r && r.data) || null); }).catch(() => { /* rules unreadable → treated as not released */ }).then(() => { clearTimeout(t); done(); });
    return () => { live = false; clearTimeout(t); };
  }, [user, p.distribusiPengiriman, fieldRulesTick]);
  const fieldRulesReady = !!user && fieldRulesFor === user.id;
  // DISTRIBUSI HP — phone width + the owner's release switch (hpUiDefault) + this user's own choice decide
  // whether the Distribusi screens open in the phone layout (one pure rule: HPLOGIC.prefState).
  uEh(() => { let v = null; try { v = user && window.HPLOGIC ? localStorage.getItem(window.HPLOGIC.prefKey(user.id)) : null; } catch (e) { v = null; } setHpSaved(v); }, [user && user.id]);
  const hpPref = window.HPLOGIC ? window.HPLOGIC.prefState({ phone: hpPhone, role: user && user.role, rules: fieldRules, saved: hpSaved }) : { eligible: false, released: false, ui: 'old' };
  const hpOn = hpPref.ui === 'new';
  const setHpUi = (ui) => { if (!user || !window.HPLOGIC) return; try { localStorage.setItem(window.HPLOGIC.prefKey(user.id), ui); } catch (e) { /* private mode */ } setHpSaved(ui); };
  const hpDock = hpOn && !!screen && screen.indexOf('dist-') === 0;
  const hpTry = hpPref.eligible && !hpPref.released && hpPref.ui === 'old' && !!screen && screen.indexOf('dist-') === 0;
  uEh(() => { document.body.classList.toggle('dhp-dock', hpDock); return () => document.body.classList.remove('dhp-dock'); }, [hpDock]);
  // `manageUsers` is a NEW cap: an override saved before it existed omits it. Derive an
  // ABSENT value from the legacy `reset` toggle or the role default — mirrors the server's
  // resolvePerms exactly, so the sidebar and the API agree on who may administer users.
  if (p.manageUsers === undefined) p.manageUsers = !!(p.reset || (FS.perms(user ? user.role : 'cashier') || {}).manageUsers);

  // Per-user BUSINESS-UNIT access (Stage A): the header selector, the active-unit view context and
  // the default unit for new records are all constrained to the unit(s) this user may access. The
  // SERVER already enforces this on every read (unitWhere); the client just mirrors it so the UI
  // never offers a unit whose data the API would withhold. `unitScope` on the self user is 'all'
  // (or absent → every unit, unchanged) or an array of businessUnitIds.
  const unitScope = (() => {
    const raw = user && user.unitScope;
    if (raw == null || raw === 'all' || raw === '') return null;   // full access
    if (Array.isArray(raw)) { const a = raw.filter(Boolean); return a.length ? a : null; }
    try { const j = JSON.parse(raw); if (Array.isArray(j)) { const a = j.filter(Boolean); return a.length ? a : null; } } catch (e) {}
    return null;
  })();
  const allowedUnits = unitScope ? businessUnits.filter((u) => unitScope.includes(u.id)) : businessUnits;
  // STAGE B — distribution is the water business, mapped wholesale to unit "air". A user scoped to
  // only mfg/nsn has no distribution access; the server 403s every /distribusi endpoint and the
  // client shows a clear "no access" notice instead of the (empty) screens. 'all'/'air' users pass.
  const canDistribution = !unitScope || unitScope.includes('air');
  // Which modules are active for the CURRENT view.
  // FULL-ACCESS BYPASS (the fix): a user with all-unit access (unitScope 'all' → null) is NEVER
  // narrowed by module toggles — they manage every unit, so they always see every module their caps
  // allow. Per-unit module hiding applies only to unit-SCOPED users. This guarantees owner/GM never
  // lose a feature no matter how any unit's enabledModules is configured.
  // Scoped users: single unit → that unit's enabledModules; "Semua" → the UNION across the units they
  // can see (so nothing needed disappears in the combined view). Unknown/absent unit → all (default).
  const activeModules = (() => {
    if (unitScope === null) return new Set(MODULE_KEYS);   // full access → every module, always
    if (activeUnit !== 'all') return unitModuleSet(businessUnits.find((u) => u.id === activeUnit));
    const pool = (allowedUnits && allowedUnits.length) ? allowedUnits : businessUnits;
    const s = new Set();
    (pool.length ? pool : [{ enabledModules: 'all' }]).forEach((u) => unitModuleSet(u).forEach((m) => s.add(m)));
    return s;
  })();
  // A nav item passes the module gate if its group isn't a module (overview/admin → always on), or
  // its module is enabled for the active unit. Distribusi additionally needs "air" access (Stage B).
  // THIS IS THE SINGLE SOURCE OF TRUTH for "is module M active for the current view" — the nav gate
  // AND the screen-level guard both derive from it, so they can never disagree.
  const navPassesModule = (grp) => {
    const mod = NAV_MODULE_OF[grp];
    if (!mod) return true;
    if (!activeModules.has(mod)) return false;
    if (mod === 'distribusi' && !canDistribution) return false;
    return true;
  };
  // Screen id → nav group (for the screen-level guard). Built from the SAME navForRole list the nav
  // uses, so a screen and its nav item always resolve to the same module.
  const navGrpOf = (() => { const m = {}; navForRole(p, user ? user.role : '').forEach((n) => { m[n.id] = n.grp; }); return m; })();
  // A module SCREEN is blocked only when its module is inactive for the current view AND we are
  // viewing a SPECIFIC unit (never in the "Semua"/union view, never for a default-'all' unit) — with
  // the one exception that a distribusi screen with no "air" access is blocked in every view (a pure
  // mfg/nsn user). Reuses navPassesModule, so nav and screen agree by construction.
  const screenModuleBlocked = (id) => {
    const grp = navGrpOf[id];
    const mod = grp && NAV_MODULE_OF[grp];
    if (!mod) return false;                 // not a module screen (overview/admin) → never blocked
    if (navPassesModule(grp)) return false; // module active for this view
    if (mod === 'distribusi' && !canDistribution) return true;   // no air access — blocked in any view
    return activeUnit !== 'all';            // module off for THIS specific unit (never surface in "Semua")
  };
  // The friendly "module off" notice for a blocked screen. A distribusi screen with no air access
  // keeps its Stage-B "no access" wording; a plain module-off gets the "enable it in Unit settings"
  // hint. (Management/overview screens are never modules, so they never reach here.)
  const moduleNotice = (id) => {
    const grp = navGrpOf[id]; const mod = grp && NAV_MODULE_OF[grp];
    const distNoAir = mod === 'distribusi' && !canDistribution;
    return (
      <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--text-mut)' }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{tr(distNoAir ? 'unit.distNoAccessTitle' : 'unit.modOffTitle')}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>{tr(distNoAir ? 'unit.distNoAccessHint' : 'unit.modOffHint')}</div>
      </div>
    );
  };
  // Keep the active-unit view context inside the user's scope: one allowed unit → fix to it (no
  // "Semua" that would imply other units); an out-of-scope selection → fall back to combined
  // (which, being server-filtered, only ever shows this user's own units anyway).
  uEh(() => {
    if (!unitScope) return;
    const ids = allowedUnits.filter((u) => u.active !== false).map((u) => u.id);
    if (ids.length === 1) { if (activeUnit !== ids[0]) setActiveUnit(ids[0]); return; }
    if (activeUnit !== 'all' && !unitScope.includes(activeUnit)) setActiveUnit('all');
  }, [user, businessUnits]);

  // Load the unit dictionary once the user is authenticated (keyed on a cap that flips true
  // after login, so the request carries a token — an at-mount fetch would 401). Placed after
  // `p` is defined so the dependency array can safely read it.
  // The unit dictionary is what makes the header selector EXIST at all (UnitSelector renders null
  // with <2 units), so a transient failure here silently removes the whole feature. The cap flips
  // true on the same render as `user`, which can still be BEFORE the cloud adapter has installed the
  // JWT — the request then 401s and the old `.catch(() => {})` swallowed it, leaving the 1-item
  // fallback forever. So: wait for a token, and retry a bounded number of times on failure.
  // Fetch the unit dictionary (incl. each unit's enabledModules) once a token is installed. Exposed
  // as a ref so a live "config" SSE event (e.g. the GM toggling a unit's modules) can re-pull it and
  // the nav/module intersection updates without a reload.
  const reloadUnits = React.useCallback(() => {
    if (!(window.API && window.API.businessUnits && window.API.getToken && window.API.getToken())) return;
    window.API.businessUnits.list()
      .then((r) => { const list = (r && r.data && r.data.length) ? r.data : null; if (list) { setBusinessUnits(list); try { localStorage.setItem('airro_bunits_cache_v1', JSON.stringify(list)); } catch (e) {} } })
      .catch(() => {});
  }, []);
  uEh(() => {
    if (!user || !(p.cashflow || p.employees) || !(window.API && window.API.businessUnits)) return;
    let live = true, tries = 0;
    const load = () => {
      if (!live) return;
      if (!(window.API.getToken && window.API.getToken())) { if (++tries <= 10) setTimeout(load, 400); return; }
      window.API.businessUnits.list()
        .then((r) => {
          if (!live) return;
          const list = (r && r.data && r.data.length) ? r.data : null;
          if (list) { setBusinessUnits(list); try { localStorage.setItem('airro_bunits_cache_v1', JSON.stringify(list)); } catch (e) {} }
        })
        .catch(() => { if (live && ++tries <= 10) setTimeout(load, 600); });
    };
    load();
    return () => { live = false; };
  }, [user, p.cashflow, p.employees]);

  // ── Browser history: hash routing (#screen) + Back-to-close overlays ─────────
  // Screen changes push a `#id` entry so the browser Back/Forward buttons walk the app
  // instead of leaving the site, refresh keeps the current screen, and screens become
  // shareable links (/#dist-deliveries). Open modals + the mobile drawer each OWN a
  // history entry, so Back dismisses them BEFORE it changes screen (essential on phones,
  // where Back is the natural "close" gesture). Everything stays permission-gated:
  // navigation and hash-restore only ever target ids present in NAV (navIdsRef).
  const isPopping = uRf(false);        // true only while applying a popstate → never push then
  const overlayStack = uRf([]);        // LIFO of close() callbacks for open modals/drawer
  const screenRef = uRf(screen); screenRef.current = screen;
  const navIdsRef = uRf([]);
  navIdsRef.current = user ? navForRole(p, user.role).map((n) => n.id) : [];
  // Bumped on every screen/sub-route popstate so a sub-routed child (distribution: customer detail,
  // tabs) re-derives its state from the URL. The screen id lives in the hash; a child's sub-state
  // (open customer, active tab, filters) lives in the query string.
  const [histTick, setHistTick] = uSh(0);
  const scrUrl = (id) => location.pathname + '#' + id;   // screen URL WITHOUT any stale query

  // Change screen + record it in history. Only ids in NAV (mirrors go()); pushing the same
  // screen is skipped so Back never lands on a duplicate. `replace` (login / perm-correction)
  // rewrites the entry instead of adding one, so Back can't return to a transient state.
  const navigate = (rawId, opts) => {
    const id = normId(rawId);   // retired ids (e.g. dist-expenses) resolve to their new home
    const replace = !!(opts && opts.replace);
    if (!navIdsRef.current.includes(id)) return;
    setDrawer(false);
    if (id === screenRef.current) { if (replace) history.replaceState({ screen: id }, '', scrUrl(id)); return; }
    setScreen(id);
    if (isPopping.current) return;                 // popstate already moved the history pointer
    const st = { screen: id };
    // A screen change clears the previous screen's query sub-state (each screen owns its own).
    if (replace) history.replaceState(st, '', scrUrl(id));
    else history.pushState(st, '', scrUrl(id));
  };

  // Overlays own one history entry each. openOverlay() sets state open + pushes a marker;
  // dismissOverlay() (X / Cancel / save) calls history.back(), whose popstate runs the
  // registered close() — so the entry and the visual state always stay in lockstep (no
  // lingering entries, no double-close). navigateFromOverlay() is for actions that close
  // the overlay AND move to a screen (drawer nav item, "Edit" in a detail modal): it
  // collapses the overlay's entry into the destination synchronously (no Back bounce).
  const openOverlay = (close) => {
    overlayStack.current.push(close);
    history.pushState({ screen: screenRef.current, overlay: overlayStack.current.length }, '', location.hash || ('#' + screenRef.current));
  };
  const dismissOverlay = () => { if (overlayStack.current.length) history.back(); };
  const navigateFromOverlay = (id) => {
    if (overlayStack.current.length) { const close = overlayStack.current.pop(); isPopping.current = true; try { close(); } finally { isPopping.current = false; } }
    if (!navIdsRef.current.includes(id)) return;
    setDrawer(false); setScreen(id);
    history.replaceState({ screen: id }, '', scrUrl(id));   // overwrite the overlay entry
  };
  // Sub-route history helper handed to distribution screens so their internal navigations (opening a
  // customer, switching a detail tab, opening the slide-over / entry modal) create REAL history
  // entries — routed through the SAME mechanism (isPopping guard + overlay stack), never a competing
  // handler. pushSub/replaceSub write the query string while keeping the current screen in the hash.
  const distNavRef = uRf(null);
  if (!distNavRef.current) {
    const subUrl = (search) => location.pathname + (search ? (search[0] === '?' ? search : '?' + search) : '') + '#' + screenRef.current;
    distNavRef.current = {
      pushSub: (search) => { if (isPopping.current) return; history.pushState({ screen: screenRef.current, sub: 1 }, '', subUrl(search)); },
      replaceSub: (search) => { history.replaceState({ screen: screenRef.current, sub: history.state && history.state.sub ? 1 : 0 }, '', subUrl(search)); },
      openOverlay: (close) => openOverlay(close),
      dismissOverlay: () => dismissOverlay(),
      isPopping: () => isPopping.current,
    };
  }
  // Overlay open helpers — set the state AND own a history entry in one call.
  const openEditing = (e) => { setEditing(e); openOverlay(() => setEditing(null)); };
  const openPw = () => { setPwModal(true); openOverlay(() => setPwModal(false)); };
  const openEmp = (emp) => { setEmpDetail(emp); openOverlay(() => setEmpDetail(null)); };
  const openDrawer = () => { setDrawer(true); openOverlay(() => setDrawer(false)); };

  // Single popstate handler (mounted once). An open overlay claims Back first: close the
  // topmost and leave the screen alone. Otherwise it's a screen navigation — restore the
  // screen from history.state (or the hash), falling back to the first NAV item if that
  // screen isn't accessible. Never pushes while handling a popstate (isPopping guard).
  uEh(() => {
    const onPop = (e) => {
      if (overlayStack.current.length) {
        const close = overlayStack.current.pop();
        isPopping.current = true;
        try { close(); } finally { isPopping.current = false; }
        return;
      }
      const st = e.state || {};
      const want = normId(st.screen || location.hash.slice(1));
      const ids = navIdsRef.current;
      const id = ids.includes(want) ? want : ids[0];
      if (!id) return;
      isPopping.current = true;
      try { setScreen(id); } finally { isPopping.current = false; }
      // Let a sub-routed screen (distribution) re-sync from the (now-restored) URL — same screen or not.
      setHistTick((t) => t + 1);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // On login / refresh: honour the URL hash if it names a screen the user may access,
  // otherwise land on their default screen. replaceState (not push) so Back can never
  // return to the login form. Runs once per login; clears any stale overlay markers.
  const bootedRef = uRf(false);
  uEh(() => {
    if (!user) { bootedRef.current = false; return; }
    if (bootedRef.current) return;
    const ids = navIdsRef.current;
    if (!ids.length) return;
    bootedRef.current = true;
    const fromHash = normId(location.hash.slice(1));
    const target = ids.includes(fromHash) ? fromHash : (ids.includes(screenRef.current) ? screenRef.current : ids[0]);
    if (target !== screenRef.current) setScreen(target);
    // Preserve the query sub-state (open customer, active tab, filters) on refresh IFF we landed on
    // the same screen the URL named — so a refresh on a customer detail restores that exact view.
    const keepSearch = target === fromHash ? location.search : '';
    history.replaceState({ screen: target }, '', location.pathname + keepSearch + '#' + target);
    overlayStack.current = [];
  }, [user]);

  // ── "New version available" (code freshness) ────────────────────────────────
  // There is NO service worker (deliberate): a tab left open across a deploy keeps the
  // old dist/app.js until reopened, which can break against a changed API. So poll the
  // cheap unauthenticated build stamp (GET /api/v1/version) every 10 min + whenever the
  // tab regains focus/visibility. If the DEPLOYED version differs from the one THIS bundle
  // was built with, show a small non-blocking banner. We NEVER auto-reload (typing is
  // never interrupted) — the user reloads on click. Shown once per detected version.
  // A 401 session-expiry is a SEPARATE flow (see API.onUnauthorized) — this is only code.
  const RUNNING_BUILD = (typeof window !== 'undefined' && window.__AIRRO_BUILD__) || null;
  const [newVer, setNewVer] = uSh(null);
  const verNotifiedRef = uRf(null);
  uEh(() => {
    if (!RUNNING_BUILD) return;   // an unstamped/dev bundle → nothing to compare against
    const base = window.AIRRO_API_BASE || '';
    let alive = true;
    const check = () => {
      if (document.hidden) return;
      fetch(base + '/version?_=' + Date.now(), { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!alive || !d || !d.version) return;   // GUARD: no/blank version → NEVER prompt
          if (d.version === RUNNING_BUILD) {         // running the deployed build → clear any banner + flag
            try { localStorage.removeItem('airro_reloaded_for'); } catch (e) {}
            if (verNotifiedRef.current) { verNotifiedRef.current = null; setNewVer(null); }
            return;
          }
          // Mismatch. If we ALREADY reloaded for this exact version and the running build is STILL old,
          // reloading didn't take (a cached bundle) — don't loop the normal prompt; show a distinct
          // "force reload" message and log both values so the mismatch is visible.
          let reloadedFor = null; try { reloadedFor = localStorage.getItem('airro_reloaded_for'); } catch (e) {}
          const stuck = reloadedFor === d.version;
          const key = d.version + (stuck ? ':stuck' : '');   // prompt once per (version, stuck) state — never loop
          if (verNotifiedRef.current !== key) {
            verNotifiedRef.current = key;
            if (stuck) console.error('[version] running build', RUNNING_BUILD, '!= deployed', d.version, '— a cached bundle survived a reload. Force reload with Ctrl+Shift+R.');
            setNewVer({ version: d.version, stuck });
          }
        })
        .catch(() => {});   // offline / API down → silently retry next tick
    };
    const iv = setInterval(check, 10 * 60 * 1000);
    const onVisible = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    const t = setTimeout(check, 15000);   // first check shortly after load (don't race hydration)
    return () => { alive = false; clearInterval(iv); clearTimeout(t); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible); };
  }, []);

  // Seed the Laporan / Biaya Tetap hub tab from an incoming screen id (old deep-links land on the tab).
  uEh(() => {
    const L = { 'acct-neraca': 'neraca', 'acct-labarugi': 'labarugi', ledger: 'bukubesar' };
    const B = { 'acct-subscriptions': 'langganan', 'acct-accrual': 'akrual', 'acct-assets': 'aset' };
    if (L[screen]) setLaporanTab(L[screen]);
    if (B[screen]) setBiayaTab(B[screen]);
  }, [screen]);

  // If the active screen isn't one the user may access (perms changed, a stale landing, or
  // a deep-linked feature), fall back to their first available screen — never render a
  // feature they don't have. Applies to EVERY menu group, not just distribusi.
  uEh(() => {
    if (!user) return;
    // Intersect with the active unit's enabled modules too, so switching to a unit that disables the
    // current screen's module bounces the user to their first still-available screen (never a
    // module the active unit turned off).
    const nav = navForRole(p, user.role).filter((n) => navPassesModule(n.grp));
    if (nav.length && !nav.some((n) => n.id === screen)) navigate(nav[0].id, { replace: true });
  }, [screen, user, activeUnit, businessUnits]);
  // Delivery notifications (AlertBell), refreshed whenever a distribusi SSE event bumps
  // distTick: (a) helpers with the board cap → today's new extra orders; (b) admins with
  // the dashboard cap → any fleet that CLOSED the day with undelivered stops (+reason).
  uEh(() => {
    if (!user || !(window.API && window.API.distribusi) || !(p.distribusiPengiriman || p.distribusiDashboard || p.distribusiBelumTerkirim)) { setDeliveryAlerts([]); setOutstandingCount(0); return; }
    let live = true; const acc = []; const jobs = [];
    if (p.distribusiPengiriman) jobs.push(window.API.distribusi.deliveries.board(FIN.TODAY, 'all').then((r) => {
      const extra = (r.data || []).filter((d) => d.source === 'tambahan' && d.status === 'pending');
      if (extra.length) acc.push({ id: 'deliv-extra', level: 'warn', icon: 'IconTruck', title: tr('nav.distDeliveries'), msg: tr('dist.newOrderAlert', { n: extra.length }) });
    }).catch(() => {}));
    // Carry-over: undelivered stops from EARLIER days — its OWN back-office cap (distribusiBelumTerkirim),
    // NOT the field-route cap. Field staff never fetch it, so no badge/alert leaks to the delivery team.
    if (p.distribusiBelumTerkirim) jobs.push(window.API.distribusi.deliveries.outstanding({ fleet: 'all' }).then((r) => {
      if (live) setOutstandingCount(r.count || 0);
      if (r.count) acc.push({ id: 'deliv-outstanding', level: 'warn', icon: 'IconTruck', title: tr('nav.distDeliveries'), msg: tr('dist.outstandingAlert', { n: r.count, d: r.oldest || 0 }) });
    }).catch(() => { if (live) setOutstandingCount(0); }));
    if (p.distribusiDashboard) jobs.push(window.API.distribusi.deliveries.closeouts('date=' + FIN.TODAY).then((r) => {
      (r.data || []).filter((c) => c.pending > 0).forEach((c) => acc.push({ id: 'deliv-close-' + c.id, level: 'warn', icon: 'IconInvoice', title: tr('dist.closeDay'), msg: tr('dist.closeAlert', { fleet: c.fleetId, y: c.pending }) }));
    }).catch(() => {}));
    Promise.all(jobs).then(() => { if (live) setDeliveryAlerts(acc); });
    return () => { live = false; };
  }, [user, p.distribusiPengiriman, p.distribusiDashboard, p.distribusiBelumTerkirim, distTick]);
  // Forgot-password requests (AlertBell) — user admins only (matches the manageUsers-gated
  // endpoint). Poll lightly; refresh on nav.
  uEh(() => {
    const isAdmin = !!(user && p.manageUsers);
    if (!isAdmin || !(window.API && window.API.users && window.API.users.resetRequests)) { setResetAlerts([]); return; }
    let live = true;
    const load = () => window.API.users.resetRequests('pending').then((r) => {
      if (!live) return; const reqs = r.data || [];
      setResetAlerts(reqs.length ? [{ id: 'pwreset', level: 'high', icon: 'IconLock', title: tr('fp.alertTitle'), msg: tr('fp.alertMsg', { n: reqs.length, u: reqs[0].username }) }] : []);
    }).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => { live = false; clearInterval(iv); };
  }, [user, screen]);   // not distTick: a distribusi write says nothing about password resets
  // Distribusi correction/void requests awaiting approval (AlertBell) — approvers only. Refreshed on
  // any distribusi SSE event (distTick) so a new request surfaces promptly.
  uEh(() => {
    if (!user || !(p.distribusiApprove || p.distribusiGantiRugiHarga) || !(window.API && window.API.distribusi && window.API.distribusi.changeRequests)) { setChangeReqAlerts([]); return; }
    let live = true;
    window.API.distribusi.changeRequests.list({ status: 'pending', fleet: 'all' }).then((r) => {
      if (!live) return; const n = (r.data || []).length;
      setChangeReqAlerts(n ? [{ id: 'distchangereq', level: 'high', icon: 'IconClock', title: tr('cr.alertTitle'), msg: tr('cr.alertMsg', { n }), screen: 'approvals' }] : []);
    }).catch(() => { if (live) setChangeReqAlerts([]); });
    return () => { live = false; };
  }, [user, p.distribusiApprove, p.distribusiGantiRugiHarga, screen, distTick]);
  // Accounting workflow attention (AlertBell) — unmapped categories or journal drift. Gated on
  // reports + the flag: when accounting v2 is off, /accounting/status 404s → no alert.
  uEh(() => {
    if (!user || !p.reports || !(window.API && window.API.accounting && window.API.accounting.status)) { setAcctAlerts([]); return; }
    let live = true;
    window.API.accounting.status({ asOf: FIN.TODAY }).then((r) => {
      if (!live) return; const s = (r && r.data) || {};
      const drift = s.integrity ? ((s.integrity.missing || 0) + (s.integrity.orphan || 0)) : 0;
      const a = [];
      if (s.unmappedCount > 0) a.push({ id: 'acctunmapped', level: 'med', icon: 'IconWarn', title: tr('map.alertTitle'), msg: tr('map.alertMsg', { n: s.unmappedCount }), screen: 'acct-mapping' });
      if (drift > 0) a.push({ id: 'acctdrift', level: 'high', icon: 'IconWarn', title: tr('flow.driftTitle'), msg: tr('flow.driftMsg', { n: drift }), screen: 'acct-backfill' });
      // Subscription due reminder — N days before nextDueDate (remindDays, default 3). Reuses the bell
      // rather than a separate mechanism; paused/stopped subs are excluded server-side.
      if (s.subsRemind > 0) a.push({ id: 'acctsubsremind', level: 'med', icon: 'IconCalendar', title: tr('subsDue.title', { n: s.subsRemind }), msg: tr('subsDue.remind', { n: s.subsRemind, amt: FIN.fmt ? FIN.fmt(s.subsRemindTotal || 0) : (s.subsRemindTotal || 0) }), screen: 'acct-subscriptions' });
      setAcctAlerts(a);
    }).catch(() => { if (live) setAcctAlerts([]); });
    return () => { live = false; };
  }, [user, p.reports, screen, distTick]);
  const catMap = uMh(() => FS.buildMap(cats), [cats]);

  // ── Setoran: REST per-record (create/update/delete one record at a time) ──
  // The old model mirrored the whole array to /state, so two users saving at once
  // clobbered each other (last-write-wins on the blob). Now each row is its own
  // REST record; a light 3s poll of GET /setoran keeps everyone current.
  // Proof photos travel as a JSON STRING in the REST record (the server proof column
  // is a string), while the app works with an object { name, isImg, data }. Serialize
  // on the way out; parse on the way back (tolerating a legacy raw data-URL string).
  // Sending the object as-is was rejected 400 by the server → surfaced as the
  // misleading "server tidak terjangkau".
  const proofToApi = (pr) => (pr == null ? null : (typeof pr === 'string' ? pr : JSON.stringify(pr)));
  const proofFromApi = (s) => {
    if (s == null || s === '') return undefined;
    if (typeof s === 'object') return s;
    // A record now stores EITHER a light ref {ref,name,isImg} (bytes in the Attachment
    // store, fetched lazily) OR a legacy inline {…,data} / raw data-URL string.
    try { const o = JSON.parse(s); if (o && typeof o === 'object' && ('ref' in o || 'data' in o)) return o; } catch (e) {}
    return /^data:/.test(s) ? { name: 'bukti', isImg: /^data:image\//.test(s), data: s } : undefined;
  };
  const setoranToApi = (r) => ({ id: r.id, date: r.date, armada: r.armada || '', galon: +r.galon || 0, cash: +r.cash || 0, bon: +r.bon || 0, bonPay: +r.bonPay || 0, expense: +r.expense || 0, note: r.note || '', proof: proofToApi(r.proof) });
  const reloadSetoran = () => {
    if (!(window.API && window.API.setoran)) return Promise.resolve();
    return window.API.setoran.list('limit=2000').then((r) => {
      if (r && Array.isArray(r.data)) { const rows = r.data.map((row) => ({ ...row, proof: proofFromApi(row.proof) })); setSetoran(rows); try { localStorage.setItem('airro_setoran_cache_v1', JSON.stringify(rows)); } catch (e) {} }
    }).catch(() => {});
  };
  const addSetoran = (rec) => {
    setSetoran((prev) => [{ ...rec }, ...prev.filter((x) => x.id !== rec.id)]);   // optimistic
    window.API.setoran.create(setoranToApi(rec)).then(reloadSetoran).catch((err) => showWriteErr(err, reloadSetoran));
  };
  const editSetoran = (rec) => {
    setSetoran((prev) => prev.map((x) => (x.id === rec.id ? { ...x, ...rec } : x)));   // optimistic
    window.API.setoran.update(rec.id, setoranToApi(rec)).then(reloadSetoran).catch((err) => showWriteErr(err, reloadSetoran));
  };
  const removeSetoran = (id) => {
    setSetoran((prev) => prev.filter((x) => x.id !== id));   // optimistic
    window.API.setoran.remove(id).then(reloadSetoran).catch((err) => showWriteErr(err, reloadSetoran));
  };
  // Initial load + light realtime poll (only for users with setoran access). Poll
  // pauses while the tab is hidden and refreshes immediately when it regains focus.
  uEh(() => {
    if (!p.setoran || !(window.API && window.API.setoran)) return;
    reloadSetoran();
    // SSE (CLOUD.onEvent → reloadSetoran) is the primary realtime path; this timer is
    // just a slow backstop in case an event is missed while the stream is down.
    const iv = setInterval(() => { if (document.visibilityState === 'visible' && window.CLOUD && window.CLOUD.active && backstopDue('setoran')) reloadSetoran(); }, 15000);
    const onVis = () => { if (document.visibilityState === 'visible') reloadSetoran(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [p.setoran]);

  // ── Cash book: REST per-record (create/update/delete one entry at a time) ──
  // Same model as setoran: each entry is its own row, so concurrent edits never
  // clobber each other. Derived setoran rows (stinc-/stmfg-) are NEVER persisted —
  // they are recomputed in-memory from the setoran table below.
  const ENTRY_TAGS = ['custPay', 'party', 'payroll', 'thr', 'orientation', 'payrollUnits'];
  const isDerivedEntry = (id) => /^st(inc|mfg)-/.test(String(id || ''));
  const entryToApi = (e) => {
    const tags = {}; ENTRY_TAGS.forEach((k) => { if (e[k] != null) tags[k] = e[k]; });
    return { id: e.id, type: e.type === 'income' ? 'income' : 'expense', amount: Math.max(0, Math.round(+e.amount || 0)),
      note: e.note || '', method: e.method || 'Cash', date: e.date, time: e.time || '00:00',
      category: e.category != null ? e.category : null, acct: e.acct != null ? e.acct : null,
      businessUnitId: e.businessUnitId || 'air',   // Stage 3 unit label (real column, not a meta tag)
      gallonQty: Math.max(0, Math.round(+e.gallonQty || 0)),   // "Pembelian Galon" → syncs a gallon purchase movement
      proof: proofToApi(e.proof), meta: Object.keys(tags).length ? JSON.stringify(tags) : null };
  };
  const apiToEntry = (row) => {
    let tags = {}; try { tags = row.meta ? JSON.parse(row.meta) : {}; } catch (e) {}
    const o = { id: row.id, type: row.type, amount: row.amount, note: row.note || '', method: row.method || 'Cash',
      date: row.date, time: row.time || '00:00', category: row.category || undefined, acct: row.acct || undefined,
      businessUnitId: row.businessUnitId || 'air', gallonQty: row.gallonQty || 0 };
    // Inter-unit transfer legs (Stage 4) — carried read-only so aggregation can eliminate the pair
    // in the combined view and the UI can offer "void" (which reverses both legs).
    if (row.interUnit) { o.interUnit = true; o.transferGroupId = row.transferGroupId || null; o.counterpartUnitId = row.counterpartUnitId || null; o.counterpartAccountId = row.counterpartAccountId || null; }
    const pf = proofFromApi(row.proof); if (pf) o.proof = pf;
    if (row.createdBy && row.createdBy.name) o.createdBy = { name: row.createdBy.name, role: row.createdBy.role || null };   // "input by" snapshot
    if (row.createdById) o.createdById = row.createdById;   // identity → drives "My Activity"
    if (row.createdAt) o.createdAt = new Date(row.createdAt).getTime();
    return Object.assign(o, tags);
  };
  const reloadEntries = () => {
    if (!(window.API && window.API.entries)) return Promise.resolve();
    return window.API.entries.list('limit=5000').then((r) => {
      if (r && Array.isArray(r.data)) { const rows = r.data.map(apiToEntry); setRealEntries(rows); try { localStorage.setItem('airro_cashbook_cache_v1', JSON.stringify(rows)); } catch (e) {} }
    }).catch(() => {});
  };
  // A write can fail two very different ways, and conflating them is the "shown an outage that did not
  // exist" bug: a NETWORK failure (no response / timeout / 5xx) is genuinely "server tidak terjangkau"
  // and worth retrying; a 4xx is the server ANSWERING and rejecting the payload — its real message must
  // be shown, and retrying it forever is pointless. `!err.status` catches ApiOffline (no status set).
  const isNetworkErr = (err) => !!(err && (err.offline || !err.status || err.status >= 500));
  // Surface a write failure: log the body (always), then either the offline toast (network) or the
  // server's ACTUAL message (4xx). `reload` re-syncs the optimistic change to the server's truth.
  const showWriteErr = (err, reload, permKey) => {
    try { console.error('[write] failed', err && err.status, err && err.body); } catch (e) {}
    if (reload) reload();
    if (isNetworkErr(err)) { setToast(tr('st.syncErr')); return; }
    const key = err.status === 403 ? (permKey || 'toast.noPerm') : err.status === 413 ? 'att.saveTooBig' : null;
    setToast(key ? tr(key) : ((err && err.message) || tr('st.saveFailed')));
  };
  // Edit/delete failures: toast the real reason (never queue a rejected 4xx). Add is handled in addEntry
  // so the form can also show the message inline on the offending field.
  const entryErr = (op) => (err) => showWriteErr(err, reloadEntries, op === 'delete' ? 'toast.noDeletePerm' : 'toast.noPerm');
  const addEntry = (e) => {
    if (isDerivedEntry(e.id)) return Promise.resolve();   // derived rows are in-memory only
    // Stamp the creator optimistically (the server does the authoritative stamp from
    // the token); reloadEntries then replaces it with the server's snapshot.
    const optimistic = { ...apiToEntry(entryToApi(e)), createdBy: e.createdBy || (user ? { name: user.name, role: user.role } : null), createdById: e.createdById || (user ? user.id : undefined), createdAt: e.createdAt || Date.now() };
    setRealEntries((prev) => [optimistic, ...prev.filter((x) => x.id !== e.id)]);
    if (!(window.API && window.API.entries)) return Promise.resolve();
    return window.API.entries.create(entryToApi(e)).then(reloadEntries).catch((err) => {
      showWriteErr(err, reloadEntries);
      // A 4xx is a real rejection — REJECT so the form keeps the input and shows the message inline on
      // the field. A network error is swallowed (optimistic row stays; treated as pending), so the caller
      // still resolves and the form clears — the entry is on screen and will re-sync.
      if (!isNetworkErr(err)) throw err;
    });
  };
  const editEntry = (e) => {
    if (isDerivedEntry(e.id)) return;
    // Editing must not drop the original creator — preserve it on the optimistic row.
    setRealEntries((prev) => prev.map((x) => (x.id === e.id ? { ...apiToEntry(entryToApi(e)), createdBy: x.createdBy || null } : x)));
    if (window.API && window.API.entries) window.API.entries.update(e.id, entryToApi(e)).then(reloadEntries).catch(entryErr('edit'));
  };
  const removeEntry = (id) => {
    if (isDerivedEntry(id)) return;   // can't delete a derived row; it regenerates from setoran
    setRealEntries((prev) => prev.filter((x) => x.id !== id));   // optimistic
    if (window.API && window.API.entries) window.API.entries.remove(id).then(reloadEntries).catch(entryErr('delete'));
  };
  // Initial load + light realtime poll (only for users with cash-book access).
  uEh(() => {
    if (!p.cashflow || !(window.API && window.API.entries)) return;
    reloadEntries();
    const iv = setInterval(() => { if (document.visibilityState === 'visible' && window.CLOUD && window.CLOUD.active && backstopDue('entries')) reloadEntries(); }, 15000);
    const onVis = () => { if (document.visibilityState === 'visible') reloadEntries(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [p.cashflow]);

  // setoran → cash flow, DERIVED (in-memory, never persisted). Whenever setoran
  // rows or cost/gallon change, each day's linked income (deposit) + manufacturing
  // expense are recomputed from the setoran REST table. These rows carry
  // setoranDay/setoranMfg tags and stable ids (stinc-/stmfg-DAY); they are merged
  // into `entries` for display/stats but are excluded from the Entry table.
  const setoranEntries = uMh(() => {
    const salesCat = (cats.income.find((c) => /refill|galon|jual|sales/i.test(c.label)) || cats.income[0] || {}).key || 'Refill';
    const supCat = (cats.expense.find((c) => /supplies|produksi|pabrik|bottling|manufact/i.test(c.label)) || cats.expense[0] || {}).key || 'Supplies';
    const cashAcct = (accounts.find((a) => a.id === settings.setoranAcct) || accounts.find((a) => a.type === 'cash') || accounts[0] || {}).id;
    const bankAcct = (accounts.find((a) => a.type === 'bank') || accounts[0] || {}).id;
    // Production-cost treatment (settings.mfgAcct): undefined → charge the bank account
    // (legacy default); '__reference__' → non-cash reference only (charges NO account,
    // still counts toward margin/reports); an account id → charge that account.
    const mfgMode = settings.mfgAcct;
    const mfgReference = mfgMode === '__reference__';
    const mfgAcctId = (mfgMode && !mfgReference && accounts.some((a) => a.id === mfgMode)) ? mfgMode : bankAcct;
    const costPer = +settings.costPerGalon || 0;
    const byDay = {};
    setoran.forEach((r) => { (byDay[r.date] = byDay[r.date] || []).push(r); });
    const out = [];
    Object.keys(byDay).forEach((day) => {
      const items = byDay[day];
      const totalSetoran = items.reduce((s, r) => s + FS.setoranOf(r), 0);
      const galon = items.reduce((s, r) => s + (+r.galon || 0), 0);
      // Setoran auto-entries belong to the WATER business → always "Air" (Stage 3). Keeping
      // them on Air means existing behaviour and every combined total is unchanged. (Cross-unit
      // posting like "Air pays Manufaktur" is Stage 4 — not built here.)
      if (totalSetoran !== 0) out.push({ id: 'stinc-' + day, type: 'income', category: salesCat, amount: totalSetoran,
        acct: cashAcct, note: tr('st.noteEntry', { d: day, n: galon, c: items.length }), method: 'Cash', date: day, time: '18:00', setoranDay: day, businessUnitId: 'air', proof: (items.find((r) => r.proof) || {}).proof });
      const mfg = galon * costPer;
      if (mfg > 0) {
        const note = tr('st.mfgNote', { d: day, n: galon, c: FIN.fmt(costPer) }) + (mfgReference ? ' · ' + (tr('st.mfgRefTag') || 'acuan') : '');
        out.push(mfgReference
          ? { id: 'stmfg-' + day, type: 'expense', category: supCat, amount: mfg, acct: null, reference: true, note, method: 'Reference', date: day, time: '18:05', setoranMfg: day, businessUnitId: 'air' }
          : { id: 'stmfg-' + day, type: 'expense', category: supCat, amount: mfg, acct: mfgAcctId, note, method: 'Transfer', date: day, time: '18:05', setoranMfg: day, businessUnitId: 'air' });
      }
    });
    return out;
  }, [setoran, settings.costPerGalon, settings.setoranAcct, settings.mfgAcct, cats, accounts, lang]);

  // The cash book the whole app reads: derived setoran rows + persisted real
  // entries. Every existing consumer (stats, reports, ledger, alerts) keeps using
  // `entries` unchanged; only the WRITE paths were rerouted to REST.
  const entries = uMh(() => [...setoranEntries, ...realEntries], [setoranEntries, realEntries]);

  // ── Roster: REST per-record (create/update/offboard one employee at a time) ──
  // Reads are allowed for any role that consumes the roster (payroll/reports/kasbon/
  // approvals/company/attendance), matching the server. WRITES require the
  // `employees` capability server-side; a rejected write shows a message and reverts.
  const canViewRoster = !!(p.employees || p.payroll || p.reports || p.company || p.kasbon || p.approvals || p.attendance);
  const staffRef = uRf(hrdStaff);
  uEh(() => { staffRef.current = hrdStaff; }, [hrdStaff]);
  const cacheStaff = (arr) => { try { localStorage.setItem('airro_staff_cache_v1', JSON.stringify(arr)); } catch (e) {} };
  const reloadStaff = () => {
    if (!canViewRoster || !(window.API && window.API.employees)) return Promise.resolve();
    return window.API.employees.list('includeInactive=true').then((r) => {
      if (r && Array.isArray(r.data)) { staffRef.current = r.data; setHrdStaff(r.data); cacheStaff(r.data); }
    }).catch(() => {});
  };
  const staffWriteErr = (err) => { setToast(tr(err && err.status === 403 ? 'toast.noStaffPerm' : 'st.syncErr')); reloadStaff(); };
  const cleanStaff = (s) => { const c = { ...s }; delete c._isNew; return c; };
  const restCreateStaff = (s) => { if (window.API && window.API.employees) window.API.employees.create(cleanStaff(s)).then(reloadStaff).catch(staffWriteErr); };
  const restUpdateStaff = (s) => { if (window.API && window.API.employees) window.API.employees.update(s.id, cleanStaff(s)).then(reloadStaff).catch(staffWriteErr); };
  const restDeleteStaff = (id) => { if (window.API && window.API.employees) window.API.employees.remove(id).then(reloadStaff).catch(staffWriteErr); };
  const staffSame = (a, b) => { try { return JSON.stringify(a) === JSON.stringify(b); } catch (e) { return false; } };
  // Diff-based per-record persistence: whatever array transform an HR screen makes
  // (upsert one, delete one, bulk dept-rename many) is translated to the matching
  // REST calls. Optimistic — state updates immediately; reloadStaff re-syncs to the
  // server's truth (also reverts an unauthorized optimistic change).
  const applyStaff = (updater) => {
    const prev = staffRef.current || [];
    const next = typeof updater === 'function' ? updater(prev) : updater;
    staffRef.current = next; setHrdStaff(next); cacheStaff(next);
    // Only holders of the `employees` write cap persist. Others (e.g. an automatic
    // late-deduction sync fired while a payroll-only user views a detail) update
    // locally and harmlessly revert on the next reloadStaff — no 403 spam. Explicit
    // edit controls are already UI-gated on p.employees.
    if (!p.employees) return;
    const prevById = new Map(prev.map((s) => [s.id, s]));
    const nextIds = new Set(next.map((s) => s.id));
    next.forEach((s) => { const b = prevById.get(s.id); if (!b) restCreateStaff(s); else if (!staffSame(b, s)) restUpdateStaff(s); });
    prev.forEach((s) => { if (!nextIds.has(s.id)) restDeleteStaff(s.id); });
  };
  // Initial load + light backstop poll for anyone who can view the roster.
  uEh(() => {
    if (!canViewRoster || !(window.API && window.API.employees)) return;
    reloadStaff();
    const iv = setInterval(() => { if (document.visibilityState === 'visible' && window.CLOUD && window.CLOUD.active && backstopDue('staff')) reloadStaff(); }, 20000);
    const onVis = () => { if (document.visibilityState === 'visible') reloadStaff(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [canViewRoster]);

  // ── Kasbon: REST per-record (submit via /request persists as pending; approve/
  // reject via /:id/approve|reject) — gated on the `kasbon` capability. ──
  const reloadCashbons = () => {
    if (!p.kasbonView || !(window.API && window.API.cashbon)) return Promise.resolve();
    return window.API.cashbon.list().then((r) => {
      if (r && Array.isArray(r.data)) { setCashbons(r.data); try { localStorage.setItem('airro_cashbon_cache_v1', JSON.stringify(r.data)); } catch (e) {} }
    }).catch(() => {});
  };
  // A kasbon returned by API.cashbon.request is ALREADY persisted → just merge + reload.
  const onAddCashbon = (cb) => { setCashbons((prev) => [cb, ...(prev || []).filter((x) => x.id !== cb.id)]); reloadCashbons(); };
  // Approve → set status + disbursedDate (the ACC date; drives the deduction cycle).
  // Reject → status only. Cancel (an already-approved kasbon) → status 'cancelled',
  // which removes its computed payroll deduction automatically (withCashbon).
  const onDecideCashbon = (id, status, reason, disbursedDate) => {
    setCashbons((prev) => prev.map((c) => (c.id === id ? { ...c, status, ...(status === 'approved' ? { disbursedDate: disbursedDate || c.disbursedDate || FIN.TODAY } : {}), ...(status === 'rejected' ? { rejectReason: reason || '' } : {}) } : c)));   // optimistic
    const call = status === 'approved' ? window.API.cashbon.approve(id, { disbursedDate: disbursedDate || FIN.TODAY })
      : status === 'cancelled' ? window.API.cashbon.cancel(id)   // submitter-of-pending OR approver; server enforces
        : window.API.cashbon.reject(id, { reason: reason || '' });
    call.then(reloadCashbons).catch((e) => { setToast(tr(e && e.status === 403 ? 'toast.noPerm' : 'st.syncErr')); reloadCashbons(); });
  };
  // Delete a kasbon (kasbonApprove only). The server refuses to delete an approved/
  // paid kasbon (must be cancelled first) → surface that as a clear message.
  const onRemoveCashbon = (id) => {
    setCashbons((prev) => (prev || []).filter((c) => c.id !== id));   // optimistic
    if (window.API && window.API.cashbon) window.API.cashbon.remove(id).then(reloadCashbons)
      .catch((e) => { setToast(tr(e && e.status === 403 ? 'toast.noPerm' : (e && e.status === 400 ? 'kb.delApprovedErr' : 'st.syncErr'))); reloadCashbons(); });
  };
  const onUpdateCashbon = (id, patch) => {
    setCashbons((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));   // optimistic
    if (window.API && window.API.cashbon) window.API.cashbon.update(id, patch).then(reloadCashbons).catch((e) => { setToast(tr(e && e.status === 403 ? 'toast.noPerm' : 'st.syncErr')); reloadCashbons(); });
  };
  uEh(() => {
    if (!p.kasbonView || !(window.API && window.API.cashbon)) return;
    reloadCashbons();
    const iv = setInterval(() => { if (document.visibilityState === 'visible' && window.CLOUD && window.CLOUD.active && backstopDue('cashbons')) reloadCashbons(); }, 20000);
    const onVis = () => { if (document.visibilityState === 'visible') reloadCashbons(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [p.kasbonView]);

  // ── Approvals: REST per-record — gated on the `approvals` capability. ──
  const apprRef = uRf(approvals);
  uEh(() => { apprRef.current = approvals; }, [approvals]);
  const cacheApprovals = (arr) => { try { localStorage.setItem('airro_approvals_cache_v1', JSON.stringify(arr)); } catch (e) {} };
  const reloadApprovals = () => {
    if (!p.approvals || !(window.API && window.API.approvals)) return Promise.resolve();
    return window.API.approvals.list().then((r) => {
      if (r && Array.isArray(r.data)) { apprRef.current = r.data; setApprovals(r.data); cacheApprovals(r.data); }
    }).catch(() => {});
  };
  const apprErr = (e) => { setToast(tr(e && e.status === 403 ? 'toast.noPerm' : 'st.syncErr')); reloadApprovals(); };
  const apprSame = (a, b) => { try { return JSON.stringify(a) === JSON.stringify(b); } catch (e) { return false; } };
  // Diff each array transform (submit one / decide one / remove one) into per-record
  // POST/PATCH/DELETE. Optimistic; reloadApprovals re-syncs (and reverts on 403).
  const applyApprovals = (updater) => {
    const prev = apprRef.current || [];
    const next = typeof updater === 'function' ? updater(prev) : updater;
    apprRef.current = next; setApprovals(next); cacheApprovals(next);
    if (!p.approvals || !(window.API && window.API.approvals)) return;
    const prevById = new Map(prev.map((a) => [a.id, a]));
    const nextIds = new Set(next.map((a) => a.id));
    next.forEach((a) => { const b = prevById.get(a.id); if (!b) window.API.approvals.create(a).then(reloadApprovals).catch(apprErr); else if (!apprSame(b, a)) window.API.approvals.update(a.id, a).then(reloadApprovals).catch(apprErr); });
    prev.forEach((a) => { if (!nextIds.has(a.id)) window.API.approvals.remove(a.id).then(reloadApprovals).catch(apprErr); });
  };
  uEh(() => {
    if (!p.approvals || !(window.API && window.API.approvals)) return;
    reloadApprovals();
    const iv = setInterval(() => { if (document.visibilityState === 'visible' && window.CLOUD && window.CLOUD.active && backstopDue('approvals')) reloadApprovals(); }, 20000);
    const onVis = () => { if (document.visibilityState === 'visible') reloadApprovals(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [p.approvals]);

  // ── HR calendar: REST per-record. Read/write allowed for any HR/approval cap. ──
  const canViewCal = !!(p.employees || p.payroll || p.attendance || p.approvals || p.company);
  const calRef = uRf(calEvents);
  uEh(() => { calRef.current = calEvents; }, [calEvents]);
  const isVirtualEv = (id) => /^h-/.test(String(id || ''));   // computed holiday rows — never persisted
  const cacheCal = (arr) => { try { localStorage.setItem('airro_calendar_cache_v1', JSON.stringify(arr)); } catch (e) {} };
  const reloadEvents = () => {
    if (!canViewCal || !(window.API && window.API.calendar)) return Promise.resolve();
    return window.API.calendar.list().then((r) => {
      if (r && Array.isArray(r.data)) { calRef.current = r.data; setCalEvents(r.data); cacheCal(r.data); }
    }).catch(() => {});
  };
  const calErr = (e) => { setToast(tr(e && e.status === 403 ? 'toast.noPerm' : 'st.syncErr')); reloadEvents(); };
  const calSame = (a, b) => { try { return JSON.stringify(a) === JSON.stringify(b); } catch (e) { return false; } };
  const calToApi = (e) => ({ id: e.id, type: e.type, title: e.title, employeeId: e.employeeId || null, startDate: e.startDate, endDate: e.endDate || '', note: e.note || '', sourceId: e.sourceId || null });
  const applyEvents = (updater) => {
    const prev = calRef.current || [];
    const next = typeof updater === 'function' ? updater(prev) : updater;
    calRef.current = next; setCalEvents(next); cacheCal(next);
    if (!(window.API && window.API.calendar)) return;
    const prevById = new Map(prev.map((e) => [e.id, e]));
    const nextIds = new Set(next.map((e) => e.id));
    next.forEach((e) => { if (isVirtualEv(e.id)) return; const b = prevById.get(e.id); if (!b) window.API.calendar.create(calToApi(e)).then(reloadEvents).catch(calErr); else if (!calSame(b, e)) window.API.calendar.update(e.id, calToApi(e)).then(reloadEvents).catch(calErr); });
    prev.forEach((e) => { if (!isVirtualEv(e.id) && !nextIds.has(e.id)) window.API.calendar.remove(e.id).then(reloadEvents).catch(calErr); });
  };
  uEh(() => {
    if (!canViewCal || !(window.API && window.API.calendar)) return;
    reloadEvents();
    const iv = setInterval(() => { if (document.visibilityState === 'visible' && window.CLOUD && window.CLOUD.active && backstopDue('events')) reloadEvents(); }, 20000);
    const onVis = () => { if (document.visibilityState === 'visible') reloadEvents(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, [canViewCal]);

  // ── Config (accounts + categories): off the /state block-mirror. Accounts use the
  // /accounts replace-collection sync (client ids preserved); categories persist as
  // a /settings key. Low-conflict, so whole-object writes are fine; the win is
  // escaping the 3s poll-revert. Writes require the `settings` cap. ──
  const cacheAccounts = (arr) => { try { localStorage.setItem('airro_accounts_cache_v1', JSON.stringify(arr)); } catch (e) {} };
  const cacheCats = (o) => { try { localStorage.setItem('airro_cats_cache_v1', JSON.stringify(o)); } catch (e) {} };
  const reloadAccounts = () => {
    if (!p.seeMoney || !(window.API && window.API.accounts)) return Promise.resolve();
    return window.API.accounts.list().then((r) => {
      if (r && Array.isArray(r.data) && r.data.length) { setAccounts(r.data); cacheAccounts(r.data); }
    }).catch(() => {});
  };
  const reloadCats = () => {
    if (!(window.API && window.API.settings)) return Promise.resolve();
    return window.API.settings.get('airro_cats').then((r) => {
      const v = r && r.data && r.data.value;
      if (v && Array.isArray(v.income) && Array.isArray(v.expense)) { setCats(v); cacheCats(v); }
    }).catch(() => {});
  };
  const applyAccounts = (updater) => {
    setAccounts((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      cacheAccounts(next);
      if (p.settings && window.API && window.API.accounts) window.API.accounts.sync(next).catch((e) => { setToast(tr(e && e.status === 403 ? 'toast.noPerm' : 'st.syncErr')); reloadAccounts(); });
      return next;
    });
  };
  const applyCats = (updater) => {
    setCats((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      cacheCats(next);
      if (p.settings && window.API && window.API.settings) window.API.settings.set('airro_cats', next).catch((e) => { setToast(tr(e && e.status === 403 ? 'toast.noPerm' : 'st.syncErr')); reloadCats(); });
      return next;
    });
  };
  // ── Config singletons via the REST /settings key-value store (off /state). Each
  // is one JSON key; low-conflict, whole-object write. Writes gate per-key on the
  // server (finance settings → 'settings'; HR keys → payroll/attendance/employees).
  const settingSlice = (key, cacheKey, setState, canWrite) => {
    const cache = (v) => { try { localStorage.setItem(cacheKey, JSON.stringify(v)); } catch (e) {} };
    const reload = () => (window.API && window.API.settings) ? window.API.settings.get(key).then((r) => { const v = r && r.data ? r.data.value : undefined; if (v !== undefined && v !== null) { setState(v); cache(v); } }).catch(() => {}) : Promise.resolve();
    const apply = (updater) => setState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      cache(next);
      if (canWrite && window.API && window.API.settings) window.API.settings.set(key, next).catch((e) => { setToast(tr(e && e.status === 403 ? 'toast.noPerm' : 'st.syncErr')); reload(); });
      return next;
    });
    return { reload, apply };
  };
  const settingsSlice = settingSlice('airro_settings', 'airro_settings_cache_v1', setSettings, !!p.settings);
  const ratesSlice = settingSlice('airro_hrd_rates', 'airro_hrd_rates_cache_v1', setHrdRates, !!(p.payroll || p.attendance || p.settings));
  const budgetSlice = settingSlice('airro_hr_budget', 'airro_hr_budget_cache_v1', setHrBudget, !!(p.payroll || p.settings));
  const deptSlice = settingSlice('airro_departments', 'airro_departments_cache_v1', setDepartments, !!(p.payroll || p.employees || p.settings));
  const posSlice = settingSlice('airro_positions', 'airro_positions_cache_v1', setPositions, !!(p.payroll || p.employees || p.settings));
  const projSlice = settingSlice('airro_projects', 'airro_projects_cache_v1', setProjects, !!(p.company || p.payroll || p.settings));
  const applySettings = settingsSlice.apply, applyRates = ratesSlice.apply, applyBudget = budgetSlice.apply, applyDepartments = deptSlice.apply, applyPositions = posSlice.apply, applyProjects = projSlice.apply;
  // Fleet is a plain string array → a /settings key (write cap: settings|setoran).
  const fleetSlice = settingSlice('airro_fleet', 'airro_fleet_cache_v1', setFleet, !!(p.setoran || p.settings));
  const applyFleet = fleetSlice.apply;
  // Transfers → /transfers replace-collection sync. Frontend uses from/to; the table
  // uses fromId/toId (FK to Account). Read cap 'cashflow', write cap 'addEntry'.
  const transferToApi = (t) => ({ id: t.id, fromId: t.from, toId: t.to, amount: Math.max(0, Math.round(+t.amount || 0)), date: t.date, note: t.note || '' });
  const apiToTransfer = (r) => ({ id: r.id, from: r.fromId, to: r.toId, amount: r.amount, date: r.date, note: r.note || '' });
  const reloadTransfers = () => {
    if (!p.cashflow || !(window.API && window.API.transfers)) return Promise.resolve();
    return window.API.transfers.list('limit=5000').then((r) => {
      if (r && Array.isArray(r.data)) { const rows = r.data.map(apiToTransfer); setTransfers(rows); try { localStorage.setItem('airro_transfers_cache_v1', JSON.stringify(rows)); } catch (e) {} }
    }).catch(() => {});
  };
  const applyTransfers = (updater) => {
    setTransfers((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try { localStorage.setItem('airro_transfers_cache_v1', JSON.stringify(next)); } catch (e) {}
      if (p.addEntry && window.API && window.API.transfers) window.API.transfers.sync(next.map(transferToApi)).catch((e) => { setToast(tr(e && e.status === 403 ? 'toast.noPerm' : 'st.syncErr')); reloadTransfers(); });
      return next;
    });
  };
  // ── Attendance + orientation attendance: the whole nested map mirrors to a REST
  // /settings key (off /state, so no poll-revert). CO writes fire a hook → debounced
  // push; a remote pull hydrates the local map + bumps syncTick so on-demand readers
  // re-render. Demo seeding is disabled in the store (months start empty). ──
  const canViewAtt = !!(p.attendance || p.payroll || p.employees || p.empDetail);
  const canWriteAtt = !!(p.attendance || p.payroll || p.employees);
  const attTimer = uRf(null), oriTimer = uRf(null);
  const pushAtt = () => { clearTimeout(attTimer.current); attTimer.current = setTimeout(() => { if (canWriteAtt && window.API && window.API.settings) window.API.settings.set('airro_attendance', CO.rawAtt()).catch(() => {}); }, 600); };
  const pushOriAtt = () => { clearTimeout(oriTimer.current); oriTimer.current = setTimeout(() => { if (canWriteAtt && window.API && window.API.settings) window.API.settings.set('airro_oriatt', CO.rawOriAtt()).catch(() => {}); }, 600); };
  const reloadAtt = () => (canViewAtt && window.API && window.API.settings) ? window.API.settings.get('airro_attendance').then((r) => { const v = r && r.data ? r.data.value : null; if (v && typeof v === 'object') { CO.hydrateAtt(v); setSyncTick((t) => t + 1); } }).catch(() => {}) : Promise.resolve();
  const reloadOriAtt = () => (canViewAtt && window.API && window.API.settings) ? window.API.settings.get('airro_oriatt').then((r) => { const v = r && r.data ? r.data.value : null; if (v && typeof v === 'object') { CO.hydrateOriAtt(v); setSyncTick((t) => t + 1); } }).catch(() => {}) : Promise.resolve();
  const pushAttRef = uRf(pushAtt), pushOriRef = uRf(pushOriAtt);
  pushAttRef.current = pushAtt; pushOriRef.current = pushOriAtt;
  uEh(() => { CO.setAttHooks(() => pushAttRef.current && pushAttRef.current(), () => pushOriRef.current && pushOriRef.current()); return () => CO.setAttHooks && CO.setAttHooks(null, null); }, []);
  // Roles list (dynamic) — load into FS (drives FS.perms) + state (re-render).
  const reloadRoles = () => {
    if (!(window.API && window.API.roles)) return Promise.resolve();
    return window.API.roles.list().then((r) => {
      if (r && Array.isArray(r.data) && r.data.length) { FS.setRoles(r.data); setRolesState(r.data); try { localStorage.setItem('airro_roles_cache_v1', JSON.stringify(r.data)); } catch (e) {} }
    }).catch(() => {});
  };
  const reloadConfig = () => { reloadAccounts(); reloadCats(); settingsSlice.reload(); ratesSlice.reload(); budgetSlice.reload(); deptSlice.reload(); posSlice.reload(); projSlice.reload(); fleetSlice.reload(); reloadTransfers(); reloadAtt(); reloadOriAtt(); reloadRoles(); reloadUnits(); };
  uEh(() => {
    if (!(window.API && window.API.accounts)) return;
    reloadConfig();
    const onVis = () => { if (document.visibilityState === 'visible') reloadConfig(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [p.seeMoney, p.payroll]);

  // Re-read every data slice from the (server-hydrated) stores into React state.
  // NOTE: this covers every slice held in shell state. On-demand stores read
  // directly inside components (attendance `airro_attendance_v2`, orientation
  // attendance `airro_oriatt_v1`) are refreshed via the `syncTick` prop instead.
  // Used after login and whenever the cloud poll pulls remote changes.
  // Apply a slice into React state ONLY when its content actually changed. A poll
  // that pulled (say) setoran must not also hand a brand-new-but-identical array to
  // setEntries/setAccounts/etc — that needless setState would re-run the setoran →
  // cash-flow derivation and re-persist those keys on a passive client, exactly the
  // churn that used to make the poll treat them as "dirty". We compare against the
  // last-applied JSON snapshot (a ref, so it's immune to closure staleness).
  const appliedRef = uRf({});
  const applySlice = (name, val, setter) => {
    const s = JSON.stringify(val);
    if (appliedRef.current[name] === s) return;
    appliedRef.current[name] = s;
    setter(val);
  };
  const refreshAllSlices = () => {
    // Everything shared is REST-loaded now (entries, setoran, staff, cashbons,
    // approvals, calendar, accounts, cats, settings, rates, budget, departments,
    // projects, fleet, transfers). Only the users list remains blob-backed.
    applySlice('users', FS.loadUsers(), setUsers);
  };

  const login = (u) => {
    FS.setSession(u.id); setUser(u); setScreen(FS.landingScreen(u.role));
    // Backend session active → the cloud adapter hydrated localStorage from the
    // server; re-pull ALL slices so the UI shows the shared data. Entries & setoran
    // live in REST tables (not the blob), so pull them explicitly too.
    if (window.CLOUD && window.CLOUD.active) { refreshAllSlices(); reloadEntries(); reloadSetoran(); reloadStaff(); reloadCashbons(); reloadApprovals(); reloadEvents(); reloadConfig(); }
  };
  const logout = () => { if (window.CLOUD) window.CLOUD.logout(); FS.setSession(null); setUser(null); setFieldPrefs((x) => (window.FIELDAPI ? window.FIELDAPI.sessionPrefs(x) : {})); setDrawer(false); overlayStack.current = []; try { history.replaceState(null, '', location.pathname); } catch (e) {} };
  // Self profile edit (display name + avatar colour only — server rejects anything
  // else). Reflect the new name/colour in the signed-in user + users list so every
  // profile card updates immediately; role/permissions are left untouched.
  const updateProfile = (data) => {
    if (!(window.API && window.API.auth && window.API.auth.updateProfile)) return Promise.reject(new Error('offline'));
    return window.API.auth.updateProfile(data).then((u) => {
      setUser((prev) => ({ ...prev, name: u.name, color: u.color }));
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, name: u.name, color: u.color } : x)));
      setToast(tr('pm.saved'));
      return u;
    });
  };

  // Restore a backend session from a persisted JWT on reload.
  uEh(() => {
    if (user || !window.CLOUD || !window.API || !window.API.getToken()) return;
    let live = true;
    window.CLOUD.restore().then((cu) => { if (live && cu) login(cu); });
    return () => { live = false; };
  }, []);

  // Auto-refresh: when the cloud poll detects remote changes, re-read slices.
  // (The poll never overwrites a key with an unsynced local edit, so re-reading
  // here can't clobber a pending local change.)
  uEh(() => {
    if (!window.CLOUD) return;
    window.CLOUD.onSync = () => { refreshAllSlices(); setSyncTick((t) => t + 1); };
    window.CLOUD.onStatus = (s) => setSyncStatus(s);
    // Token expired mid-session → prompt a re-login (unsynced local edits are kept
    // and flushed after signing back in).
    window.CLOUD.onSessionExpired = () => setSessionExpired(true);
    // SSE notice for a non-/state REST entity (setoran) or a tab-focus resync →
    // re-fetch that entity immediately. Near-0 latency; the 3s poll is now a backstop.
    window.CLOUD.onEvent = (evt) => {
      if (!evt) return;
      if (evt.entity === 'setoran' || evt.entity === 'focus') reloadSetoran();
      if (evt.entity === 'entry' || evt.entity === 'focus') reloadEntries();
      if (evt.entity === 'employee' || evt.entity === 'focus') reloadStaff();
      if (evt.entity === 'cashbon' || evt.entity === 'focus') reloadCashbons();
      if (evt.entity === 'approval' || evt.entity === 'focus') reloadApprovals();
      if (evt.entity === 'calendar' || evt.entity === 'focus') reloadEvents();
      if (evt.entity === 'config') reloadConfig();
      else if (evt.entity === 'focus' && Date.now() - configFocusAt >= CONFIG_FOCUS_MIN_MS) { configFocusAt = Date.now(); reloadConfig(); }
      if (evt.entity === 'role') reloadRoles();
      if (evt.entity === 'distribusi' || evt.entity === 'focus') setDistTick((t) => t + 1);   // Distribusi dashboard self-refetches
      if (evt.entity === 'distribusi' && evt.action === 'rules') setFieldRulesTick((t) => t + 1);   // owner changed Aturan lapangan / released the new view
    };
    return () => { if (window.CLOUD) { window.CLOUD.onSync = null; window.CLOUD.onStatus = null; window.CLOUD.onEvent = null; window.CLOUD.onSessionExpired = null; } };
  }, []);

  // Lock the page scroll behind the mobile drawer while it's open.
  uEh(() => {
    document.body.classList.toggle('drawer-lock', drawer);
    return () => document.body.classList.remove('drawer-lock');
  }, [drawer]);

  // New manual entries default to the active-unit context (Air when "Semua"). The AddEntry
  // form also carries its own selector; this is the fallback so a unit is always stamped.
  // Returns the write promise so the form can await it — clear on success, show the server's message
  // inline on a 4xx. The "saved" toast fires only when the write is accepted (or optimistically queued
  // offline), never when the server rejected the payload.
  const add = (e) => addEntry({ ...e, businessUnitId: e.businessUnitId || unitDefaultForNew() })
    .then(() => setToast(tr(e.type === 'income' ? 'toast.incomeSaved' : 'toast.expenseSaved', { amt: FIN.fmt(e.amount) })));
  // Upsert one staff into the roster (used by orientation actions + detail edits).
  const upsertStaff = (s) => applyStaff((prev) => { const clean = { ...s }; delete clean._isNew; return prev.find((x) => x.id === s.id) ? prev.map((x) => x.id === s.id ? clean : x) : [...prev, clean]; });
  // ---- Orientation actions (new-hire lifecycle) ----
  // Graduating passes the employee out of the orientation/DW bucket into a payroll
  // stage (permanent/contract/probation) → they move to Data Karyawan automatically.
  const graduateOrientation = (s, targetStage) => { const stage = ['permanent', 'contract', 'probation'].indexOf(targetStage) >= 0 ? targetStage : 'permanent'; upsertStaff({ ...s, stage, orientation: { ...(s.orientation || {}), outcome: 'passed', endDate: HRD.orientationEnd(s) } }); setToast(tr('ori.toastPassed', { n: s.name, st: tr('stage.' + stage) })); };
  const failOrientation = (s) => { upsertStaff({ ...s, orientation: { ...(s.orientation || {}), outcome: 'failed' }, sepStatus: 'orientation_failed', active: false, separationDate: FIN.TODAY, separationReason: tr('ori.failReason') }); setToast(tr('ori.toastFailed', { n: s.name })); };
  // Orientation lump sum already posted to the cash book (by staff id) — no double post.
  const orientationPaidIds = uMh(() => (entries || []).filter((e) => e.orientation).map((e) => e.orientation), [entries]);
  const payOrientation = (s, alsoRecordExpense) => {
    const total = HRD.orientationTotal(s, CO.oriAtt(s.id), hrdRates);
    upsertStaff({ ...s, orientation: { ...(s.orientation || {}), paid: true, paidAt: FIN.TODAY } });
    if (alsoRecordExpense && total > 0 && !orientationPaidIds.includes(s.id)) {
      const bank = accounts.find((a) => a.type === 'bank') || accounts[0] || {};
      const entry = { id: 'e' + Date.now().toString(36), type: 'expense', category: 'Orientation', amount: total, acct: bank.id,
        note: tr('ori.expenseNote', { n: s.name }), method: 'Transfer BCA', date: FIN.TODAY, time: '09:00', orientation: s.id };
      addEntry(entry);
    }
    setToast(tr('ori.toastPaid', { amt: FIN.fmt(total) }));
  };
  const del = (id) => { if (!p.delete) { setToast(tr('toast.onlyOwnerDelete')); return; } const e = entries.find((x) => x.id === id); if (e && !confirm(tr('toast.deleteConfirm', { n: e.note || '', amt: FIN.fmt(e.amount || 0) }))) return; removeEntry(id); setToast(tr('toast.deleted')); };
  const saveEdit = (upd) => { editEntry(upd); dismissOverlay(); setToast(tr('toast.updated')); };
  // Edit dispatcher for the entry lists. A normal entry opens the per-entry modal. A
  // Setoran-derived row (stinc-/stmfg-) is an auto-generated summary of the Setoran
  // table — it can't be edited here (the change would be recomputed away, the old
  // "account won't save" revert), so its edit button takes the user to the Setoran
  // screen where the source lives (or explains it if they have no setoran access).
  const editEntryRow = (e) => {
    if (isDerivedEntry(e.id)) { if (p.setoran) navigate('setoran'); else setToast(tr('entries.derivedInfo')); return; }
    // Inter-unit legs can't be edited in isolation (that would desync the pair) — offer to VOID
    // the whole transfer, which reverses BOTH legs.
    if (e.interUnit) { if (p.interUnitTransfer) voidInterUnit(e); else setToast(tr('iu.noPerm')); return; }
    openEditing(e);
  };
  // Create an inter-unit transfer (linked pair) via the atomic endpoint, then re-sync the cash
  // book so both legs appear.
  const doInterUnitTransfer = (payload) => {
    if (!(window.API && window.API.interUnitTransfers)) return Promise.reject();
    return window.API.interUnitTransfers.create(payload).then((r) => { reloadEntries(); setToast(tr('iu.posted', { amt: FIN.fmt(payload.amount) })); return r; });
  };
  // Void an inter-unit transfer → reverses BOTH legs (permanent). Confirmed first.
  const voidInterUnit = (e) => {
    if (!e.transferGroupId || !(window.API && window.API.interUnitTransfers)) return;
    if (!confirm(tr('iu.voidConfirm', { amt: FIN.fmt(e.amount) }))) return;
    window.API.interUnitTransfers.void(e.transferGroupId).then(() => { reloadEntries(); setToast(tr('iu.voided')); }).catch((err) => setToast(tr(err && err.status === 403 ? 'iu.noPerm' : 'st.syncErr')));
  };
  // Demo reset now clears the REST cash book (real entries only; derived setoran
  // rows regenerate). Deletes each persisted entry, then repaints empty.

  const range = PERIOD.resolveRange(gran, anchor);
  const periodLbl = PERIOD.periodLabel(gran, anchor, range);
  const monthKey = anchor.slice(0, 7);                 // for payroll / alerts / tips (month-based)
  const nextDisabled = range.end >= FIN.TODAY;

  // ── Business-unit VIEW scoping (Stage 3) ──
  // The finance screens read SCOPED copies of the entries/accounts arrays. With activeUnit ===
  // 'all' the scoped arrays ARE the full arrays (identity), so every number is byte-for-byte the
  // pre-Stage-3 value. Under a unit they narrow to that unit's records. Accounts marked 'shared'
  // (Bersama) show only in the combined view, so their balance is never double-counted.
  const scopedEntries = uMh(() => (activeUnit === 'all' ? entries : entries.filter((e) => unitOfRec(e) === activeUnit)), [entries, activeUnit]);
  const scopedAccounts = uMh(() => (activeUnit === 'all' ? accounts : accounts.filter((a) => unitOfRec(a) === activeUnit)), [accounts, activeUnit]);
  // HR follows the SAME view context: employees carry businessUnitId (Stage 2), so a unit view shows
  // only that unit's people and every payroll total recomputes from that set. With 'all' the scoped
  // array IS the full array, so combined totals are byte-for-byte unchanged and Σ(units) == combined.
  // The payroll screen's own per-unit chips/subtotals (Stage 2) keep working inside whatever set they
  // are handed — no duplicated filtering logic.
  const scopedStaff = uMh(() => (activeUnit === 'all' ? hrdStaff : hrdStaff.filter((s) => unitOfRec(s) === activeUnit)), [hrdStaff, activeUnit]);
  // Label for the "Unit: X" indicator (null in the combined view → no indicator shown).
  const activeUnitName = uMh(() => (activeUnit === 'all' ? null : ((businessUnits.find((u) => u.id === activeUnit) || {}).name || activeUnit)), [activeUnit, businessUnits]);

  // `combined` = the "Semua" view. In it, inter-unit transfer legs are ELIMINATED from company
  // income/expense/profit (internal trade nets to zero — not new company revenue/cost); they are
  // still counted in CASH (they moved money between accounts and net to zero, so combined cash is
  // unchanged). In a single-unit view every leg counts as that unit's real income/expense.
  const computeStats = (ents, accts, combined) => {
    let balIn = 0, balOut = 0;
    ents.forEach((e) => { if (!e.reference) { if (e.type === 'income') balIn += e.amount; else balOut += e.amount; } });   // cash inflow/outflow (opening added below)
    // Period Pemasukan/Pengeluaran come from the SHARED reducer — the SAME function the Transaksi
    // screen uses — so Ringkasan and Transaksi can never drift (guarded by finance-totals.test.js).
    const pl = FTOT.periodTotals(ents, range.start, range.end, combined);
    const profit = pl.income - pl.expense;
    const openingTotal = accts.reduce((s, a) => s + (+a.opening || 0), 0);
    return { balance: openingTotal + balIn - balOut, opening: openingTotal, totalIn: balIn, totalOut: balOut, income: pl.income, expense: pl.expense, profit, margin: pl.income ? Math.round((profit / pl.income) * 1000) / 10 : 0, monLabel: periodLbl };
  };
  // Cash Balance = REAL cash on hand = opening balances + every inflow − every outflow (all-time),
  // over the SCOPED sets. NOT the same as Net Profit (period income − expense, no opening).
  const stats = uMh(() => computeStats(scopedEntries, scopedAccounts, activeUnit === 'all'), [scopedEntries, scopedAccounts, activeUnit, range.start, range.end, periodLbl]);
  // THE cash figure — one number for every "cash" reader (low-cash alert, dashboard "Total kas", Kas &
  // Bank total). It is Σ per-account balance over the shown accounts + "Belum dipetakan", honouring
  // account attribution + transfers (unlike stats.balance, an opening+income−expense proxy that ignored
  // both — the source of the "two different cash numbers on one screen" bug). Matches invariant #12.
  const cashOnHand = uMh(() => FS.cashOnHand(scopedAccounts, entries, accounts, transfers), [scopedAccounts, entries, accounts, transfers]);
  // Reports read a P&L-only view: in "Semua" the inter-unit legs are removed entirely (they are
  // internal), while a single-unit report keeps that unit's own leg.
  const reportEntries = uMh(() => (activeUnit === 'all' ? entries.filter((e) => !e.interUnit) : scopedEntries), [entries, scopedEntries, activeUnit]);

  // Per-unit breakdown for the combined view — its columns sum EXACTLY to the combined stats
  // (the Stage-3 invariant, mirroring payroll). Only shown on the overview when >1 unit is present.
  const unitStats = uMh(() => {
    if (businessUnits.length < 2) return [];
    const rows = businessUnits.filter((u) => u.active !== false).map((u) => ({
      id: u.id, name: u.name,
      // Single-unit semantics (combined=false) → each unit's own inter-unit leg counts as its
      // real income/expense. Entries always belong to a REAL unit; openings may be 'shared'.
      t: computeStats(entries.filter((e) => unitOfRec(e) === u.id), accounts.filter((a) => unitOfRec(a) === u.id), false),
    }));
    // 'shared' (Bersama) accounts belong to no single unit — surface them as their own row so
    // the rows still sum EXACTLY to the combined total (no double-count, nothing dropped).
    const sharedAccts = accounts.filter((a) => a.businessUnitId === 'shared');
    if (sharedAccts.length) rows.push({ id: 'shared', name: tr('bu.shared'), t: computeStats([], sharedAccts, false) });
    const kept = rows.filter((u) => u.t.totalIn || u.t.totalOut || u.t.opening);
    // Inter-unit ELIMINATION row: the per-unit rows count internal legs (they're each unit's real
    // income/expense), but the combined total excludes them. Show the removed amounts as a
    // negative "internal" row so per-unit rows + elimination === the combined total (its cash
    // effect is zero). Only appears when there ARE inter-unit legs in the period.
    const iu = entries.filter((e) => e.interUnit && e.date >= range.start && e.date <= range.end);
    if (iu.length) {
      const inc = iu.filter((e) => e.type === 'income').reduce((s, e) => s + e.amount, 0);
      const exp = iu.filter((e) => e.type === 'expense').reduce((s, e) => s + e.amount, 0);
      kept.push({ id: '__iu__', name: tr('unit.internal'), elim: true, t: { income: -inc, expense: -exp, balance: 0 } });
    }
    return kept;
  }, [entries, accounts, businessUnits, range.start, range.end, periodLbl, lang]);

  const deltas = uMh(() => {
    const pm = PERIOD.prevMatched(gran, anchor, null, null, FIN.TODAY);
    const curEnd = pm.curEnd < range.end ? pm.curEnd : range.end;
    const cur = PERIOD.aggregate(scopedEntries, range.start, curEnd);
    const prv = PERIOD.aggregate(scopedEntries, pm.start, pm.end);
    return { income: PERIOD.pctDelta(cur.income, prv.income), expense: PERIOD.pctDelta(cur.expense, prv.expense), profit: PERIOD.pctDelta(cur.profit, prv.profit) };
  }, [scopedEntries, gran, anchor, range.start, range.end]);

  // GROSS-PROFIT LADDER — Penjualan − Biaya produksi (HPP) = Laba kotor − Beban operasional = Laba
  // bersih. It PARTITIONS the same period-scoped set `stats` sums (income + expense, inter-unit legs
  // eliminated in the combined view), via the ONE shared classifier (FCLS), so Income is never netted
  // and the parts reconcile: penjualan == stats.income; produksi+operasional+lain == stats.expense;
  // labaBersih == stats.profit. Same helper feeds the 12-month trend + the Transaksi tabs.
  const prodCats = uMh(() => FCLS.buildProdCats(cats), [cats]);
  const ladderRows = (start, end) => scopedEntries.filter((e) => e.date >= start && e.date <= end && !(activeUnit === 'all' && e.interUnit));
  const ladder = uMh(() => FCLS.split(ladderRows(range.start, range.end), prodCats), [scopedEntries, range.start, range.end, activeUnit, prodCats]);
  const ladderDeltas = uMh(() => {
    const pm = PERIOD.prevMatched(gran, anchor, null, null, FIN.TODAY);
    const curEnd = pm.curEnd < range.end ? pm.curEnd : range.end;
    const cur = FCLS.split(ladderRows(range.start, curEnd), prodCats);
    const prv = FCLS.split(ladderRows(pm.start, pm.end), prodCats);
    return { penjualan: PERIOD.pctDelta(cur.penjualan, prv.penjualan), produksi: PERIOD.pctDelta(cur.produksi, prv.produksi), operasional: PERIOD.pctDelta(cur.operasional, prv.operasional), labaKotor: PERIOD.pctDelta(cur.labaKotor, prv.labaKotor), labaBersih: PERIOD.pctDelta(cur.labaBersih, prv.labaBersih) };
  }, [scopedEntries, gran, anchor, range.start, range.end, activeUnit, prodCats]);
  // Drill from a ladder line (or KPI) into the transactions behind it: 'entries:<tab>' opens Transaksi
  // on that tab so the owner can immediately see which rows make the number up.
  const drillTo = (target) => {
    if (typeof target === 'string' && target.indexOf('entries:') === 0) { const tab = target.slice(8); go('entries'); setTxnForm(false); changeTxnTab(tab); return; }
    go(target);
  };

  const curMonthKey = FIN.TODAY.slice(0, 7);
  const curPayLabel = FIN.MONTHS[+curMonthKey.split('-')[1] - 1] + ' ' + curMonthKey.split('-')[0];

  const last7 = uMh(() => {
    const arr = []; const end = new Date(FIN.TODAY + 'T00:00');
    for (let i = 6; i >= 0; i--) { const d = new Date(end); d.setDate(end.getDate() - i); arr.push({ date: d.toISOString().slice(0, 10), income: 0, expense: 0 }); }
    const byDate = {}; arr.forEach((a) => byDate[a.date] = a);
    scopedEntries.forEach((e) => { const a = byDate[e.date]; if (a) { if (e.type === 'income') a.income += e.amount; else a.expense += e.amount; } });
    return arr;
  }, [scopedEntries]);

  const breakdown = uMh(() => {
    const map = {};
    scopedEntries.filter((e) => e.type === 'expense' && e.date >= range.start && e.date <= range.end).forEach((e) => { map[e.category] = (map[e.category] || 0) + e.amount; });
    const total = Object.values(map).reduce((a, b) => a + b, 0);
    return { total, segs: Object.entries(map).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ key: k, label: FS.catInfo(catMap, k).label, value: v, pct: total ? Math.round((v / total) * 100) : 0 })) };
  }, [scopedEntries, range.start, range.end, catMap]);

  const today = uMh(() => {
    let income = 0, expense = 0, count = 0;
    scopedEntries.forEach((e) => { if (e.date === FIN.TODAY) { count++; if (e.type === 'income') income += e.amount; else expense += e.amount; } });
    return { income, expense, count };
  }, [scopedEntries]);

  const recent = uMh(() => scopedEntries.slice().sort(FS.byNewest).slice(0, 12), [scopedEntries]);
  const periodEntries = uMh(() => scopedEntries.filter((e) => e.date >= range.start && e.date <= range.end).sort(FS.byNewest), [scopedEntries, range.start, range.end]);

  // TRANSAKSI split (presentation only) — [Semua] [Setoran] [Operasional]. Setoran are deposits from
  // distribusi; operasional is the owner's own bookkeeping. Default tab is configurable
  // (settings.txnDefaultTab), falling back to "operasional" (what the finance user came to do). The
  // active tab lives in the URL (?tab=) so a view survives refresh and can be shared.
  const [txnTab, setTxnTab] = uSh(FINSRC.normalizeTab(new URLSearchParams(location.search).get('tab')) || FINSRC.normalizeTab(settings.txnDefaultTab) || 'operasional');
  const changeTxnTab = (t) => {
    t = FINSRC.normalizeTab(t) || 'operasional'; setTxnTab(t);
    try { history.replaceState(history.state || { screen: 'entries' }, '', location.pathname + '?tab=' + t + '#' + (screenRef.current || 'entries')); } catch (e) {}
  };
  // The two sides come straight from the SHARED predicate — a TOTAL split, so ops ∪ setoran === all.
  const opsPeriod = uMh(() => periodEntries.filter((e) => FINSRC.entrySource(e) === 'operasional'), [periodEntries]);
  const setoranPeriod = uMh(() => periodEntries.filter((e) => FINSRC.entrySource(e) === 'setoran'), [periodEntries]);
  // Function split (FCLS) for the Setoran · Produksi · Operasional tabs — one vocabulary with the ladder.
  const produksiPeriod = uMh(() => periodEntries.filter((e) => window.FCLS.classify(e, window.FCLS.buildProdCats(cats)) === 'produksi'), [periodEntries, cats]);
  const operasionalPeriod = uMh(() => periodEntries.filter((e) => { const k = window.FCLS.classify(e, window.FCLS.buildProdCats(cats)); return k === 'operasional' || k === 'lain'; }), [periodEntries, cats]);
  // The Setoran tab renders the underlying Setoran records (per fleet per day) so it can show armada
  // detail; filter them to the active period. Read-only — corrections live in Distribusi → Setoran.
  const setoranRowsPeriod = uMh(() => (setoran || []).filter((r) => r.date >= range.start && r.date <= range.end), [setoran, range.start, range.end]);

  // "Saldo Berjalan" in the Operasional table = the REAL running balance of each row's OWN account
  // (opening + that account's movements — every non-reference entry + transfers — chronologically up to
  // the row). So a funded account reads a true positive balance; an account whose opening was never
  // entered reads negative (never hidden — the D2 banner + invariant #12 explain it). Keyed by entry id.
  const opsRunningById = uMh(() => {
    const ids = new Set(accounts.map((a) => a.id));
    const primary = accounts.length ? accounts[0].id : null;
    const acctOf = (e) => (!e.acct ? primary : (ids.has(e.acct) ? e.acct : null));
    const bal = {}; accounts.forEach((a) => { bal[a.id] = +a.opening || 0; });
    const moves = [];
    entries.forEach((e) => { if (e.reference) return; const aid = acctOf(e); if (aid == null) return; moves.push({ k: (e.date || '') + (e.time || ''), aid, delta: (e.type === 'income' ? 1 : -1) * e.amount, id: e.id }); });
    (transfers || []).forEach((t) => { const k = (t.date || '') + '00:00'; if (bal[t.from] != null) moves.push({ k, aid: t.from, delta: -(+t.amount || 0) }); if (bal[t.to] != null) moves.push({ k, aid: t.to, delta: +(+t.amount || 0) }); });
    moves.sort((a, b) => (a.k < b.k ? -1 : a.k > b.k ? 1 : 0));
    const out = {};
    for (const m of moves) { bal[m.aid] += m.delta; if (m.id) out[m.id] = bal[m.aid]; }
    return out;
  }, [entries, accounts, transfers]);
  // D2: cash/bank accounts whose balance is NEGATIVE and whose opening was never entered — surfaced as a
  // banner on the transaction list (do NOT hide/clamp). The invariant "no negative balance" stays failing.
  const openingNeededAccts = uMh(() => (scopedAccounts || accounts).filter((a) => !(+a.opening) && FS.acctBalance(a, entries, accounts, transfers) < 0).map((a) => ({ id: a.id, name: a.name })), [scopedAccounts, accounts, entries, transfers]);

  // ENTRY FORM lives ONLY on Transaksi now (Ringkasan is read-only). It is opened by [Transaksi Baru]
  // or the shareable deep link ?new=income|expense (which survives refresh — keepSearch keeps the query
  // when the hash already resolves to #entries). The global period + active unit are shell state, so
  // they carry across the Ringkasan→Transaksi jump with nothing retyped.
  const [txnFormOpen, setTxnFormOpen] = uSh(() => !!new URLSearchParams(location.search).get('new'));
  const [txnNewType, setTxnNewType] = uSh(() => (new URLSearchParams(location.search).get('new') === 'income' ? 'income' : 'expense'));
  const setTxnForm = (open, type) => {
    setTxnFormOpen(open); if (type) setTxnNewType(type);
    try {
      const url = open ? location.pathname + '?new=' + (type || txnNewType || 'expense') + '#entries' : location.pathname + '#entries';
      history.replaceState(history.state || { screen: 'entries' }, '', url);
    } catch (e) {}
  };
  // [Catat Transaksi] on Ringkasan → go to Transaksi with the form open (period + unit already global).
  const catatTransaksi = (type) => { go('entries'); setTxnForm(true, type || 'expense'); };
  // Deep-link / redirect (item 5): a ?new= link (even an old one that lands on another screen) opens the
  // form on Transaksi rather than 404ing. Runs once on mount.
  uEh(() => {
    if (new URLSearchParams(location.search).get('new') && screenRef.current !== 'entries') { go('entries'); setTxnFormOpen(true); }
  }, []);

  const prevAgg = uMh(() => {
    const [y, m] = monthKey.split('-').map(Number);
    let pm = m - 1, py = y; if (pm <= 0) { pm = 12; py--; }
    const key = `${py}-${String(pm).padStart(2, '0')}`;
    let income = 0, expense = 0; const days = new Set();
    entries.forEach((e) => { if (e.date.startsWith(key)) { days.add(e.date); e.type === 'income' ? income += e.amount : expense += e.amount; } });
    return { income, expense, days: days.size };
  }, [entries, monthKey]);

  const monthStats = uMh(() => {
    let mIn = 0, mOut = 0; const days = new Set();
    entries.forEach((e) => { if (e.date.startsWith(monthKey)) { days.add(e.date); e.type === 'income' ? mIn += e.amount : mOut += e.amount; } });
    return { income: mIn, expense: mOut, profit: mIn - mOut, margin: mIn ? Math.round(((mIn - mOut) / mIn) * 1000) / 10 : 0, days: days.size };
  }, [entries, monthKey]);

  // approved leave → auto-mark attendance days as leave
  const applyLeaveToAtt = (req) => {
    if (!req || req.type !== 'leave' || !req.from || !req.to) return;
    const mk = req.from.slice(0, 7);
    const sid = req.staffId || (hrdStaff.find((s) => s.name === req.who) || {}).id;
    CO.applyLeave(sid, mk, req.from, req.to);
    // Mirror the approved leave onto the shared HR calendar (dedupe by sourceId) —
    // now a per-record POST /calendar instead of a blob append.
    const already = (calRef.current || []).some((e) => e.sourceId && e.sourceId === req.id);
    if (!already) applyEvents((prev) => [...prev, { id: CO.newEventId(), type: 'leave', title: req.title || 'Cuti', employeeId: sid || null, startDate: req.from, endDate: req.to || req.from, note: req.detail || '', sourceId: req.id }]);
  };

  // approved deduction request → add to that employee's payroll deductions. Tag it
  // with reqId so cancelling/deleting the request can find & remove exactly this one.
  const approveDeduction = (req) => {
    if (!req || !req.staffId || !req.amount) return;
    applyStaff((prev) => prev.map((s) => s.id === req.staffId
      ? { ...s, deductions: [...(s.deductions || []), { id: CO.newReqId(), label: req.title || 'Potongan', amount: +req.amount, reqId: req.id }] }
      : s));
  };

  const submitRequest = (req) => { applyApprovals((prev) => [req, ...prev]); setToast(tr('req.submitted')); };

  // Undo the side-effects of an APPROVED request so nothing is left dangling when it
  // is cancelled/deleted: the leave mirror on the calendar + the attendance leave
  // marking, and the salary deduction it created.
  const reverseApproval = (a) => {
    if (!a) return;
    if (a.type === 'leave' && a.from && a.to) {
      const sid = a.staffId || (hrdStaff.find((s) => s.name === a.who) || {}).id;
      if (sid) CO.clearLeave(sid, a.from.slice(0, 7), a.from, a.to);
      applyEvents((prev) => (prev || []).filter((e) => e.sourceId !== a.id));
    }
    if (a.type === 'deduction' && a.staffId) {
      applyStaff((prev) => prev.map((s) => s.id === a.staffId ? { ...s, deductions: (s.deductions || []).filter((d) => d.reqId !== a.id) } : s));
    }
  };
  // Requester cancels their own still-pending request → status 'cancelled' (+ trail).
  const cancelRequest = (a) => {
    if (!a) return;
    applyApprovals((prev) => prev.map((x) => x.id === a.id ? { ...x, status: 'cancelled', cancelledBy: user.name, cancelledAt: Date.now() } : x));
    setToast(tr('req.cancelledToast'));
  };
  // Delete a request from the list. If it was approved, undo its effects first.
  const deleteRequest = (a) => {
    if (!a) return;
    if (a.status === 'approved') reverseApproval(a);
    applyApprovals((prev) => (prev || []).filter((x) => x.id !== a.id));
    setToast(tr('req.deletedToast'));
  };

  // keep auto late-penalty deduction + overtime pay in sync on each staff record
  const syncLateDeduct = (staffId, amount, label, otAmount) => {
    applyStaff((prev) => prev.map((s) => {
      if (s.id !== staffId) return s;
      const manual = (s.deductions || []).filter((d) => !d.auto);
      const cur = (s.deductions || []).find((d) => d.auto && d.id === 'auto-late');
      const newDeds = amount > 0 ? [...manual, { id: 'auto-late', label, amount, auto: true }] : manual;
      const sameDed = (amount > 0 ? (cur && +cur.amount === amount) : !cur);
      const sameOt = (+s.otPay || 0) === (+otAmount || 0);
      if (sameDed && sameOt) return s;
      return { ...s, deductions: newDeds, otPay: +otAmount || 0 };
    }));
  };

  const salaryCatKey = uMh(() => {
    const m = cats.expense.find((c) => /salar|gaji|wage|payroll/i.test(c.label));
    return (m && m.key) || (cats.expense[0] && cats.expense[0].key) || 'Salaries';
  }, [cats]);
  // Payroll totals for the current month: exclude separated staff, prorate the
  // separation month, and fold in the running cycle's kasbon — matches PayrollScreen.
  const hrdTotals = uMh(() => HRD.totals(HRD.payrollStaff(scopedStaff, curMonthKey, hrdRates).map((s) => HRD.withCashbon(s, cashbons, HRD.payCycle().anchor)), hrdRates), [scopedStaff, hrdRates, cashbons, curMonthKey]);
  const payrollPosted = uMh(() => entries.find((e) => e.payroll === curMonthKey) || null, [entries, curMonthKey]);
  const postPayroll = (amount, label, unitBreakdown) => {
    if (!p.payroll || !amount) return;
    const existing = entries.find((e) => e.payroll === curMonthKey);
    const msg = existing ? tr('hrd.repostConfirm', { amt: FIN.fmt(amount), m: label }) : tr('hrd.postConfirm', { amt: FIN.fmt(amount), m: label });
    if (!confirm(msg)) return;
    const [yy, mm] = curMonthKey.split('-');
    const lastDay = new Date(+yy, +mm, 0).getDate();
    const date = curMonthKey === FIN.TODAY.slice(0, 7) ? FIN.TODAY : `${curMonthKey}-${String(lastDay).padStart(2, '0')}`;
    const entry = { id: 'e' + Date.now().toString(36), type: 'expense', category: salaryCatKey, amount, acct: (accounts.find((a) => a.type === 'bank') || accounts[0] || {}).id,
      note: tr('hrd.payrollNote', { m: label, n: hrdStaff.length }), method: 'Transfer BCA', date, time: '09:00', payroll: curMonthKey,
      // The posting is ONE company-wide cash entry, tagged "Air" so the combined view is
      // unchanged; the per-unit split lives in payrollUnits below (splitting it into separate
      // per-unit cash entries is Stage 4). Does not affect the amount.
      businessUnitId: 'air',
      // Snapshot each unit's share AS OF this run (Stage 2). Frozen on the posted entry so a
      // later placement change never rewrites a past payslip. Does not affect the amount.
      payrollUnits: Array.isArray(unitBreakdown) ? unitBreakdown : undefined };
    realEntries.filter((e) => e.payroll === curMonthKey).forEach((e) => removeEntry(e.id));   // drop the previous month's posting
    addEntry(entry);
    // Kasbon of the payroll cycle just paid → mark settled ('paid') so they stop
    // counting. Per-record PATCH (approved/active → paid); optimistic + reload.
    const anchor = HRD.payCycle().anchor;
    (cashbons || []).forEach((c) => { if ((c.status === 'active' || c.status === 'approved') && (c.cycleAnchor || HRD.payCycle(c.date).anchor) === anchor) onUpdateCashbon(c.id, { status: 'paid' }); });
    setToast(tr(existing ? 'toast.payrollUpdated' : 'toast.payrollPosted', { amt: FIN.fmt(amount) }));
  };

  // THR posting → record holiday allowance as an expense in the cash book
  const thrPosted = uMh(() => entries.find((e) => e.thr) || null, [entries]);
  const postThr = (amount, label) => {
    if (!amount) return;
    const existing = entries.find((e) => e.thr);
    if (!confirm(tr('thr.confirm', { amt: FIN.fmt(amount), d: label }))) return;
    const entry = { id: 'e' + Date.now().toString(36), type: 'expense', category: salaryCatKey, amount,
      acct: (accounts.find((a) => a.type === 'bank') || accounts[0] || {}).id,
      note: tr('thr.noteEntry', { d: label, n: hrdStaff.length }), method: 'Transfer BCA', date: FIN.TODAY, time: '09:30', thr: true, businessUnitId: 'air' };
    realEntries.filter((e) => e.thr).forEach((e) => removeEntry(e.id));   // replace the previous THR posting
    addEntry(entry);
    setToast(tr('thr.toast', { amt: FIN.fmt(amount) }));
  };

  // Which setoran days are reflected in the cash book (derived, always true when the
  // day has a deposit) — used by the setoran screen's "posted" pill.
  const setoranPosted = uMh(() => { const m = {}; entries.forEach((e) => { if (e.setoranDay) m[e.setoranDay] = true; }); return m; }, [entries]);
  // customer payment transfers (bon/credit settled by transfer) → income entries tagged custPay
  const custPayments = uMh(() => entries.filter((e) => e.custPay).sort(FS.byNewest), [entries]);
  const addPayment = (pay) => {
    const salesCat = (cats.income.find((c) => /refill|galon|jual|sales|bon|piutang/i.test(c.label)) || cats.income[0] || {}).key || 'Refill';
    addEntry({ id: 'cp' + Date.now().toString(36), type: 'income', category: salesCat, amount: +pay.amount || 0,
      acct: pay.acct, note: tr('cp.note', { who: pay.party || '—', m: pay.method }), method: pay.method || 'Transfer', date: pay.date, time: '12:00', custPay: true, party: pay.party, businessUnitId: 'air', proof: pay.proof });
    setToast(tr('cp.toast', { amt: FIN.fmt(+pay.amount || 0) }));
  };
  const delPayment = (id) => removeEntry(id);
  // (setoran→cash-flow derivation is the `setoranEntries` memo above — computed
  // in-memory from the setoran REST table and never persisted.)

  const alerts = uMh(() => p.seeMoney
    ? ALERTS.computeAlerts({ entries, balance: cashOnHand, monthIncome: monthStats.income, monthExpense: monthStats.expense, month: monthKey, thresholds: settings, fmt: FIN.fmt, lang })
    : [], [entries, cashOnHand, monthStats, monthKey, settings, p.seeMoney, lang]);

  // "Aktivitas Saya": the current user's own recent creations, gathered from the
  // records already loaded in shell state and filtered by createdById (stable
  // identity — survives a rename and never collides with a same-named colleague).
  // Only entities the user's role can see contribute, which is exactly right.
  const myActivity = uMh(() => {
    if (!user) return [];
    const uid = user.id, items = [];
    (realEntries || []).forEach((e) => { if (e.createdById === uid) items.push({ kind: 'entry', id: e.id, title: e.note || (catMap[e.category] && catMap[e.category].label) || tr('nav.entries'), amount: e.type === 'income' ? e.amount : -e.amount, when: e.createdAt || 0, date: e.date }); });
    (cashbons || []).forEach((c) => { if (c.createdById === uid) { const emp = (hrdStaff || []).find((s) => s.id === c.employeeId); items.push({ kind: 'kasbon', id: c.id, title: emp ? emp.name : tr('nav.kasbon'), amount: -(c.amount || 0), when: c.createdAt || 0, date: c.date }); } });
    (approvals || []).forEach((a) => { if (a.createdById === uid) items.push({ kind: 'approval', id: a.id, title: a.title || tr('nav.approvals'), amount: a.amount ? -a.amount : 0, when: a.createdAt || 0, date: a.date }); });
    (hrdStaff || []).forEach((s) => { if (s.createdById === uid) items.push({ kind: 'employee', id: s.id, title: s.name, when: s.createdAt || 0, date: s.joinedDate || s.contractStart }); });
    return items.sort((a, b) => (b.when || 0) - (a.when || 0) || String(b.date || '').localeCompare(String(a.date || ''))).slice(0, 25);
  }, [user, realEntries, cashbons, approvals, hrdStaff, catMap]);

  // ---- not logged in ----
  if (!user) return <AUTH.LoginScreen onLogin={login} lang={lang} onLang={changeLang} users={users} />;

  // NAV = caps (navForRole) ∩ active-unit modules ∩ unit access. Filtering here means BOTH the
  // desktop sidebar and the mobile drawer/bottom-bar (which reuse this NAV) update together, and
  // switching the active unit re-filters the nav to that unit's enabled modules.
  const NAV = navForRole(p, user ? user.role : '').filter((n) => navPassesModule(n.grp));
  // NAV only ever contains screens the user may access, so navigating is just "go if it's
  // in NAV". A cross-screen button targeting something the user lacks silently no-ops (its
  // own control is already hidden). The 2nd arg is legacy/ignored.
  // Navigate to a screen. If an overlay owns the top history entry (the mobile drawer is
  // open), collapse it into the destination; otherwise push a normal screen entry.
  const go = (id) => { if (overlayStack.current.length) navigateFromOverlay(id); else navigate(id); };
  // Admin/settings shortcuts inside the profile menu — already perm-filtered by NAV,
  // so a non-admin simply sees none.
  const pmShortcuts = NAV.filter((n) => n.id === 'users' || n.id === 'settings' || n.id === 'hrsettings');
  const toggleGrp = (g) => setNavOpen((prev) => { const n = { ...prev, [g]: prev[g] === false ? true : false }; try { localStorage.setItem('airro_navopen_v1', JSON.stringify(n)); } catch (e) {} return n; });
  const Nav = () => (
    <>
      <div className="brand"><Logo s={32} /><div className="brand-lockup"><div className="brand-name">AirRO</div><div className="brand-desc">Reverse Osmosis</div></div></div>
      {NAV_GROUPS.map((g) => {
        const items = NAV.filter((n) => n.grp === g && !n.hidden);   // hidden = navigable-but-not-drawn (hub tab targets)
        if (!items.length) return null;
        // "Pengaturan Akuntansi" starts collapsed — it is monthly/rare work, not the daily surface.
        const collapsed = navOpen[g] === false || (navOpen[g] === undefined && g === 'acctset');
        return (
          <div key={g} className="nav-section">
            <button className={`nav-label nav-label-btn ${collapsed ? 'collapsed' : ''}`} onClick={() => toggleGrp(g)}>
              <span>{tr('navgrp.' + g)}</span><IconCaret s={13} />
            </button>
            {!collapsed && items.map((n) => (
              <button key={n.id} className={`nav-item ${screen === n.id || HUB_OF[screen] === n.id ? 'on' : ''}`} title={n.title || n.label} onClick={() => go(n.id)}>
                {Ish(n.icon, { s: 20 })}<span>{n.label}</span>{n.soon && <span className="fin-badge-soon">{tr('fin.soonBadge')}</span>}
                {n.id === 'dist-deliveries' && outstandingCount > 0 && <span className="nav-count" title={tr('dist.outstandingAlert', { n: outstandingCount, d: 0 })}>{outstandingCount}</span>}
              </button>
            ))}
          </div>
        );
      })}
    </>
  );

  const periodBar = (
    <div className="period-bar">
      <div className="gran-seg">
        {[['day', 'rep.day'], ['week', 'rep.week'], ['month', 'rep.month'], ['year', 'rep.year']].map(([g, key]) => (
          <button key={g} className={`gran-btn ${gran === g ? 'on' : ''}`} onClick={() => setGran(g)}>{tr(key)}</button>
        ))}
      </div>
      <DP.PeriodNav gran={gran} anchor={anchor} onAnchor={setAnchor} label={periodLbl} today={FIN.TODAY} />
    </div>
  );

  const titles = {
    company: { t: tr('t.company'), s: tr('s.company') },
    projects: { t: tr('t.projects'), s: tr('s.projects') },
    headcount: { t: tr('t.headcount'), s: tr('s.headcount') },
    employees: { t: tr('t.employees'), s: tr('s.employees') },
    hrsettings: { t: tr('t.hrsettings'), s: tr('s.hrsettings') },
    hrreport: { t: tr('t.hrreport'), s: tr('s.hrreport') },
    thr: { t: tr('t.thr'), s: tr('s.thr') },
    rollcall: { t: tr('t.rollcall'), s: tr('s.rollcall') },
    approvals: { t: tr('t.approvals'), s: tr('s.approvals') },
    overview: { t: tr('nav.finSummary'), s: tr('s.overview') },
    moneyspots: { t: tr('t.moneyspots'), s: tr('s.moneyspots') },
    setoran: { t: tr('t.setoran'), s: tr('s.setoran') },
    entries: { t: tr('nav.finTransaksi'), s: tr('s.entries') },
    reports: { t: tr('t.reports'), s: tr('s.reports') },
    'biaya-tetap': { t: tr('nav.finRecurring'), s: tr('s.finRecurring') },
    ledger: { t: tr('t.reports'), s: tr('s.reports') },
    'acct-neraca': { t: tr('t.reports'), s: tr('s.reports') },
    'acct-labarugi': { t: tr('t.reports'), s: tr('s.reports') },
    'acct-subscriptions': { t: tr('nav.finRecurring'), s: tr('s.finRecurring') },
    'acct-accrual': { t: tr('nav.finRecurring'), s: tr('s.finRecurring') },
    'acct-assets': { t: tr('nav.finRecurring'), s: tr('s.finRecurring') },
    reconcile: { t: tr('t.finRekon'), s: tr('s.finRekon') },
    close: { t: tr('t.finClose'), s: tr('s.finClose') },
    payroll: { t: tr('t.payroll'), s: tr('s.payroll') },
    kasbon: { t: tr('nav.kasbon'), s: tr('kb.intro') },
    settings: { t: tr('t.settings'), s: tr('s.settings') },
    users: { t: tr('t.users'), s: tr('s.users') },
  }[screen] || (screen && screen.indexOf('dist-') === 0
    ? { t: (NAV.find((n) => n.id === screen) || {}).label || tr('dist.module'), s: tr('dist.module') }
    : { t: '', s: '' });

  // MODE LAPANGAN — an account that may use the phone view lands in it FULL SCREEN: no app bar, no unit
  // banner (owner, 3D). "Kembali ke tampilan lama" (⋯ menu) returns to the old Pengiriman; the rules
  // screen opens inside the app and any other screen brings the phone view back.
  // Overlays the whole app needs on every screen — also in the full-screen field view (3D-1 review).
  const globalOverlays = (
    <>
      {toast && <FToast msg={toast} onDone={() => setToast(null)} />}
      {newVer && (
        <div className={`ver-banner ${newVer.stuck ? 'stuck' : ''}`} role="status" aria-live="polite">
          <IconRefresh s={16} />
          <span className="ver-msg">{newVer.stuck ? tr('ver.force') : tr('ver.available')}</span>
          {/* Record which build we're reloading FOR, so if the reload doesn't take (cached bundle) the
              next load detects it's stuck and switches to the force-reload message instead of looping. */}
          <button className="ver-reload" onClick={() => { try { localStorage.setItem('airro_reloaded_for', newVer.version); } catch (e) {} location.reload(); }}>{tr('ver.reload')}</button>
          <button className="ver-x" title={tr('ver.later')} aria-label={tr('ver.later')} onClick={() => setNewVer(null)}>×</button>
        </div>
      )}
      {pwModal && <AUTH.ChangePassword onClose={dismissOverlay} onDone={() => { dismissOverlay(); setToast(tr('pw.changed')); }} />}
      {sessionExpired && (
        <div className="modal-scrim" style={{ zIndex: 200 }}>
          <div className="modal-card" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-body" style={{ textAlign: 'center', padding: '10px 6px' }}>
              <span className="sess-ic"><IconLock s={26} /></span>
              <div style={{ fontSize: 18, fontWeight: 800, marginTop: 12 }}>{tr('sess.title')}</div>
              <div style={{ fontSize: 13.5, color: 'var(--text-mut)', marginTop: 8, lineHeight: 1.5 }}>{tr('sess.body')}</div>
            </div>
            <div className="modal-foot" style={{ justifyContent: 'center' }}>
              <button className="btn btn-primary" onClick={() => { setSessionExpired(false); logout(); }}>{tr('sess.login')}</button>
            </div>
          </div>
        </div>
      )}
      <PROOFMOUNT />
    </>
  );
  // M13: no flash of the finance app while the owner's release switch is still loading.
  const fieldWait = !!(window.FIELD && window.FIELDAPI && user && window.FIELDAPI.bootWait({ perms: p, role: user.role, prefs: fieldPrefs, rulesReady: fieldRulesReady, device: fieldDevice }));
  if (fieldWait) return <div className="mlap-boot" role="status" aria-label={tr('fld.loading')} />;
  const fieldFull = !!(window.FIELD && p.distribusiPengiriman && fieldPref.eligible && fieldPref.ui === 'new' && screen !== 'dist-field-rules');
  if (fieldFull) return (
    <>
    <window.FIELD.App user={user} perms={p} pref={fieldPref} device={fieldDevice} today={FIN.TODAY} onLogout={logout}
      fleetList={fleet} fleetScope={user && user.fleetScope} refreshKey={distTick}
      onExit={() => { setFieldPref({ ui: 'old' }); go('dist-deliveries'); }} onPref={setFieldPref}
      onOpenRules={p.distribusiAturanLapangan ? () => go('dist-field-rules') : null} />
    {globalOverlays}
    </>
  );
  // DISTRIBUSI HP — what every phone-layout screen gets besides the old screen's own props: the drawer,
  // the alert bell, the Catat form host, the old-view switch.
  const bellEl = (p.seeMoney || p.distribusiPengiriman || resetAlerts.length > 0 || changeReqAlerts.length > 0 || acctAlerts.length > 0) ? <ALERTS.AlertBell alerts={[...(p.seeMoney ? alerts : []), ...deliveryAlerts, ...resetAlerts, ...changeReqAlerts, ...acctAlerts]} /> : null;
  const hpEntryOpen = (kind, opts) => { setHpEntry(Object.assign({ kind }, opts || {})); openOverlay(() => setHpEntry(null)); };
  const hp = { onMenu: openDrawer, chrome: { bell: bellEl }, onEntry: hpEntryOpen, onOld: () => setHpUi('old'), perms: p, rules: fieldRules, userId: user && user.id };
  // A redesigned screen replaces the old one with EXACTLY the same props (+ hp); the rest stay as they are.
  const dhpSwap = (el) => { const C = hpOn && window.DHP && window.DHP.screens[screen]; return C ? React.createElement(C, Object.assign({}, el.props, { hp })) : el; };
  return (
    <div className="app">
      <aside className="sidebar">
        <Nav />
        <div className="user-chip">
          <span className="user-av" style={{ background: user.color, width: 38, height: 38 }}>{FS.initials(user.name)}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="up-name" title={user.name} style={{ fontSize: 13.5 }}>{user.name}</div>
            <AUTH.RoleBadge role={user.role} size="sm" />
          </div>
          <button className="icon-btn" title={tr('pw.change')} onClick={openPw}><IconLock s={17} /></button>
          <button className="icon-btn logout-btn" title="Sign out" onClick={logout}><IconLogout s={18} /></button>
        </div>
      </aside>

      <div className={`scrim ${drawer ? 'open' : ''}`} onClick={dismissOverlay} />
      <div className={`drawer ${drawer ? 'open' : ''}`}>
        <Nav />
        {hpOn && <div className="dhp-drawer-x"><AUTH.LangToggle lang={lang} onLang={changeLang} />{p.cashflow && <UnitSelector units={allowedUnits} value={activeUnit} onChange={setActiveUnit} />}</div>}
        {hpPref.eligible && <button type="button" className="dhp-drawer-sw" onClick={() => { setHpUi(hpOn ? 'old' : 'new'); dismissOverlay(); }}>{tr(hpOn ? 'dhp.useOld' : 'dhp.useNew')}</button>}
        <div className="user-chip" style={{ marginTop: 'auto' }}>
          <span className="user-av" style={{ background: user.color, width: 38, height: 38 }}>{FS.initials(user.name)}</span>
          <div style={{ minWidth: 0, flex: 1 }}><div className="up-name" title={user.name} style={{ fontSize: 13.5 }}>{user.name}</div><AUTH.RoleBadge role={user.role} size="sm" /></div>
          <button className="icon-btn" title={tr('pw.change')} onClick={openPw}><IconLock s={17} /></button>
          <button className="icon-btn logout-btn" onClick={logout}><IconLogout s={18} /></button>
        </div>
      </div>

      <main className="main">
        <div className="content">
          <header className="topbar">
            <button className="hamburger" onClick={openDrawer}><IconMenu s={22} /></button>
            <div>
              <h1>{titles.t}</h1>
              <div className="sub">{titles.s}</div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
              {(screen === 'overview' || screen === 'entries') && periodBar}
              {p.cashflow && <UnitSelector units={allowedUnits} value={activeUnit} onChange={setActiveUnit} />}
              {window.CLOUD && (window.CLOUD.active || syncStatus === 'expired') && (
                <span className={`sync-pill ${syncStatus}`} title={tr('sync.' + syncStatus)}>
                  <span className="sync-dot" /><span className="sync-txt">{tr('sync.' + syncStatus)}</span>
                </span>
              )}
              <AUTH.LangToggle lang={lang} onLang={changeLang} />
              {bellEl}
              <AUTH.ProfileMenu user={user} lang={lang} onLang={changeLang} alerts={p.seeMoney ? alerts : []} activity={myActivity}
                onChangePassword={openPw} onLogout={logout} onNavigate={go} shortcuts={pmShortcuts} onUpdateProfile={updateProfile} />
            </div>
          </header>
          {/* Which unit am I looking at? Shown on EVERY screen while a single unit is selected. */}
          <UnitViewBar unitName={activeUnitName} exempt={UNIT_EXEMPT(screen)} />

          {/* STAGE B — a user without "air" access opening any distribution screen sees a clear
              no-access notice (the API already 403s the data). Keeps the water module invisible to
              a pure mfg/nsn user without hiding the nav for everyone. */}
          {screen && screen.indexOf('dist-') === 0 && screenModuleBlocked(screen) ? moduleNotice(screen) : (<>
          {hpTry && <div className="fld-try dhp-try" role="region" aria-label={tr('dhp.tryNew')}>
            <span><b>{tr('dhp.tryNew')}</b><br /><small>{tr('dhp.tryNewSub')}</small></span>
            <button type="button" className="btn btn-primary" onClick={() => setHpUi('new')}>{tr('dhp.tryNewBtn')}</button>
          </div>}
          {screen === 'dist-dashboard' && p.distribusiDashboard && dhpSwap(
            <DIST.Dashboard refreshKey={distTick} today={FIN.TODAY} canDocCustomize={!!p.distribusiDokumenSesuaikan} isOwner={user && user.role === 'owner'}
              staffMode={!!(p.distribusi && !p.distribusiHargaMaster && !p.distribusiAudit && !p.distribusiCustomers)}
              canInput={!!p.distribusiInput} canHistory={!!p.distribusiDashHistory}
              fleetScope={user && user.fleetScope} fleet={fleet} distFleet={distFleet} setDistFleet={setDistFleet}
              onQuickInput={() => { go('dist-transactions', !p.distribusiInput); if (p.distribusiInput) setDistFormTick((t) => t + 1); }} onOpenCustomers={() => go('dist-customers', !p.distribusi)}
              onOpenTransactions={() => go('dist-transactions', !(p.distribusiInput || p.distribusiKoreksi || p.distribusiExpense))}
              onOpenDeliveries={p.distribusiPengiriman ? () => go('dist-deliveries') : null} />
          )}
          {screen === 'dist-transactions' && (p.distribusiInput || p.distribusiKoreksi || p.distribusiExpense) && dhpSwap(
            <DIST.Transactions refreshKey={distTick} openFormTick={distFormTick} today={FIN.TODAY} canDocCustomize={!!p.distribusiDokumenSesuaikan}
              staffMode={!!(p.distribusi && !p.distribusiHargaMaster && !p.distribusiAudit && !p.distribusiCustomers)}
              canInput={!!p.distribusiInput} canKoreksi={!!p.distribusiKoreksi} canVoid={!!p.distribusiVoid} canHardDelete={!!p.distribusiHardDelete} canArchive={!!p.distribusiLegacyImport} canExpense={!!p.distribusiExpense} canPrice={!!p.distribusiHargaMaster} canHapus={!!p['distribusi.transaksi.hapus']} userName={user && user.name}
              canNego={!!p.distribusiGantiRugiHarga} canNoPhoto={!!p.distribusiGantiRugiTanpaFoto} selfLimit={+p.maxSelfApproveAmount || 0}
              canViewAll={!!p['distribusi.lihat.semua']} canView7={!!p['distribusi.lihat.7hari']} canViewMonth={!!p['distribusi.lihat.bulan_ini']} canViewSisaBon={!!(p['distribusi.lihat.semua'] || p['distribusi.lihat.sisa_bon'])} maxLookback={+p.maxLookbackDays || 0}
              fleetScope={user && user.fleetScope} fleet={fleet} distFleet={distFleet} setDistFleet={setDistFleet}
              nav={distNavRef.current} histTick={histTick}
              onChanged={() => setDistTick((t) => t + 1)} />
          )}
          {screen === 'dist-customers' && p.distribusiCustomers && dhpSwap(
            <DIST.Customers refreshKey={distTick} canDocCustomize={!!p.distribusiDokumenSesuaikan} canNego={!!p.distribusiGantiRugiHarga} canNoPhoto={!!p.distribusiGantiRugiTanpaFoto} canCustomers={!!p.distribusiCustomers} canCustImport={!!p.distribusiCustomerImport} canPrice={!!p.distribusiHargaMaster} canInput={!!p.distribusiInput} canKoreksi={!!p.distribusiKoreksi} canVoid={!!p.distribusiVoid} canHardDelete={!!p.distribusiHardDelete} canInvoiceDelete={!!p.distribusiInvoiceHapus} canHapus={!!p['distribusi.transaksi.hapus']} canApprove={!!p.distribusiApprove} canApproveSelf={!!p.distribusiApproveSelf} selfApproveLimit={+p.maxSelfApproveAmount || 0} currentUserId={user && user.id} canDelete={!!p.distribusiCustomerDelete}
              canLegacyImport={!!p.distribusiLegacyImport} canArchive={!!p.distribusiLegacyImport} canBonAdjust={!!p.distribusiBonAdjust} canPenyesuaianGalon={!!p.distribusiPenyesuaianGalon} canPenyesuaianBon={!!p.distribusiPenyesuaianBon} isGmOwner={user && (user.role === 'owner' || user.role === 'gm')}
              staffMode={!!(p.distribusi && !p.distribusiHargaMaster && !p.distribusiAudit && !p.distribusiCustomers)}
              fleet={fleet} fleetScope={user && user.fleetScope} distFleet={distFleet} setDistFleet={setDistFleet} userName={user && user.name} userRole={user && user.role}
              nav={distNavRef.current} histTick={histTick}
              onGoHarga={() => go('dist-prices', !p.distribusi)} onChanged={() => setDistTick((t) => t + 1)}
              onGoApprovals={p.distribusiApprove ? () => go('approvals', false) : null}
              onOpenLoss={p.distribusiBonAdjust ? () => go('dist-loss-report', false) : null} />
          )}
          {screen === 'dist-deliveries' && p.distribusiPengiriman && (
            <>
              {fieldPref.eligible && <div className="fld-try" role="region" aria-label={tr(fieldPref.released ? 'fld.fieldView' : 'fld.tryNew')}>
                <span><b>{tr(fieldPref.released ? 'fld.fieldView' : 'fld.tryNew')}</b><br /><small>{tr(fieldDevice === 'desktop' ? 'fld.fieldViewDesk' : fieldPref.released ? 'fld.fieldViewSub' : 'fld.tryNewSub')}</small></span>
                <button type="button" className="btn btn-primary" onClick={() => setFieldPref({ ui: 'new' })}>{tr('fld.tryNewBtn')}</button>
              </div>}
              {dhpSwap(<DIST.Deliveries refreshKey={distTick} today={FIN.TODAY} canOrder={!!p.distribusiOrder} canRoute={!!p.distribusiRute} canClose={!!p.distribusiPengiriman} canKoreksi={!!p.distribusiKoreksi}
                canBelumTerkirim={!!p.distribusiBelumTerkirim}
                /* TWO separate rights: seeing the tracking panel at all, and being allowed to re-map a
                   vehicle to another armada. A driver holds the first and must never hold the second. */
                canGps={!!p.distribusiLacakArmada} canGpsMap={!!p.settings} canLoc={!!p.distribusiLokasiSimpan}
                fleetScope={user && user.fleetScope} fleet={fleet} distFleet={distFleet} setDistFleet={setDistFleet}
                onChanged={() => setDistTick((t) => t + 1)} />)}
            </>
          )}
          {screen === 'dist-field-rules' && p.distribusiAturanLapangan && window.FIELD && <window.FIELD.RulesScreen fleetList={fleet} canRelease={!!user && user.role === 'owner'} onSaved={(r) => setFieldRules(r)} onBack={() => go('dist-dashboard')} />}
          {/* Pengeluaran (field expenses) moved into the Transaksi screen — no standalone route. */}
          {screen === 'dist-delivery-report' && p.distribusiPengirimanReport && (
            <DIST.DeliveryReport refreshKey={distTick} today={FIN.TODAY}
              fleetScope={user && user.fleetScope} fleet={fleet} distFleet={distFleet} setDistFleet={setDistFleet} />
          )}
          {screen === 'dist-loss-report' && p.distribusiBonAdjust && (
            <DIST.LossReport refreshKey={distTick} today={FIN.TODAY} isOwner={user && user.role === 'owner'} isGmOwner={user && (user.role === 'owner' || user.role === 'gm')}
              fleetScope={user && user.fleetScope} fleet={fleet} distFleet={distFleet} setDistFleet={setDistFleet} onChanged={() => setDistTick((t) => t + 1)} />
          )}
          {screen === 'dist-adjust-report' && p.distribusiPenyesuaian && (
            <DIST.AdjustReport refreshKey={distTick} today={FIN.TODAY}
              fleetScope={user && user.fleetScope} fleet={fleet} distFleet={distFleet} setDistFleet={setDistFleet} />
          )}
          {screen === 'dist-gallon' && p.gudangGalonView && (
            <DIST.Gallon refreshKey={distTick} canCustomers={!!p.gudangGalonKoreksi} canOpname={!!p.gudangGalonOpname} canReset={!!p.gudangGalonReset} canGalonDelete={!!p.gudangGalonHardDelete} canResetTotal={!!p['gudang.galon.reset_total']}
              fleetScope={user && user.fleetScope} fleet={fleet} distFleet={distFleet} setDistFleet={setDistFleet} />
          )}
          {/* Galon Kita — the glass field screen inside the office content (.mlap-hg-kitaroot keeps the sidebar and
              bottom nav); the server scopes the overview to the account's armadas. */}
          {screen === 'dist-galon-kita' && p.gudangGalonView && window.HG && (
            <div className="mlap-root mlap-hg-kitaroot"><window.HG.GalonKita fleet={distFleet} isGmOwner={!!user && (user.role === 'owner' || user.role === 'gm')} canSetStale={!!p.settings} /></div>
          )}
          {screen === 'dist-prices' && p.distribusiHargaMaster && (
            <DIST.Prices refreshKey={distTick} canPrice={!!p.distribusiHargaMaster} onChanged={() => setDistTick((t) => t + 1)} />
          )}
          {screen === 'dist-integration' && p.distribusiCashIntegrasi && (
            <DIST.Integration refreshKey={distTick} today={FIN.TODAY} />
          )}
          {screen === 'dist-zones' && p.distribusiZona && (
            <DIST.Zones refreshKey={distTick} canManage={!!p.distribusiZonaKelola} canCustomers={!!p.distribusiCustomers} fleet={fleet}
              onChanged={() => setDistTick((t) => t + 1)}
              /* Deep-link into the customer's detail: the same ?c=<id>#dist-customers URL a refresh would
                 restore, handed to the shell's single popstate handler (which re-syncs the screen). */
              onOpenCustomer={(id) => { history.pushState({ screen: 'dist-customers', sub: 1 }, '', location.pathname + '?c=' + encodeURIComponent(id) + '#dist-customers'); window.dispatchEvent(new PopStateEvent('popstate', { state: history.state })); }} />
          )}
          {screen === 'dist-doc-design' && (p.distribusiDokumenDesain || p.distribusiDokumenTtd) && (
            <DocDesignScreen canDesign={!!p.distribusiDokumenDesain} canSign={!!p.distribusiDokumenTtd} onClose={() => go('dist-dashboard')} />
          )}
          {screen === 'dist-audit' && p.distribusiAudit && (
            <DIST.Audit refreshKey={distTick} canAudit={!!p.distribusiAudit} onChanged={() => setDistTick((t) => t + 1)} />
          )}
          {screen && screen.indexOf('dist-') === 0 && !['dist-dashboard', 'dist-transactions', 'dist-deliveries', 'dist-delivery-report', 'dist-loss-report', 'dist-adjust-report', 'dist-customers', 'dist-gallon', 'dist-galon-kita', 'dist-integration', 'dist-prices', 'dist-audit', 'dist-doc-design', 'dist-zones', 'dist-field-rules'].includes(screen) && <DistPlaceholder screen={screen} nav={NAV} />}
          </>)}

          {/* Warehouse (gudang) + Setoran are module screens too — show the same friendly notice when
              their module is off for the active unit (nav already hides them; this is the fallback so
              the raw API 403 never leaks). Uses the SAME screenModuleBlocked as the nav gate. */}
          {(screen === 'gudang' || screen === 'suppliers' || screen === 'setoran') && screenModuleBlocked(screen) ? moduleNotice(screen) : (<>
          {screen === 'gudang' && p.gudangView && (
            <GUDANG.Dept refreshKey={distTick} canAddStock={!!p.gudangAddStock} canKoreksi={!!p.gudangKoreksi} canBuffer={!!p.gudangBuffer}
              canItems={!!p.gudangItems} canSupplier={!!p.gudangSupplier} canDamage={!!p.gudangDamage} canReport={!!p.gudangReport}
              canGalon={!!p.distribusiGallon} onOpenGalon={() => go('dist-gallon')} fleet={fleet} today={FIN.TODAY} />
          )}
          {screen === 'suppliers' && p.gudangSupplier && (
            <GUDANG.Suppliers />
          )}

          {screen === 'setoran' && p.setoran && (
            <SETORAN.SetoranScreen setoran={setoran} onAdd={addSetoran} onEdit={editSetoran} onRemove={removeSetoran} fleet={fleet} setFleet={p.setoran ? applyFleet : null} accounts={accounts} canEdit={true} postedDays={setoranPosted} autoSynced={true} costPerGalon={settings.costPerGalon} onCostChange={(v) => applySettings((prev) => ({ ...prev, costPerGalon: v }))} depositAcct={settings.setoranAcct} onDepositAcctChange={(v) => applySettings((prev) => ({ ...prev, setoranAcct: v }))} mfgAcct={settings.mfgAcct} onMfgAcctChange={(v) => applySettings((prev) => ({ ...prev, mfgAcct: v }))} payments={custPayments} onAddPayment={addPayment} onDelPayment={delPayment} />
          )}
          </>)}

          {screen === 'moneyspots' && p.cashflow && (
            <FIN.MoneySpots accounts={accounts} setAccounts={applyAccounts} entries={entries} transfers={transfers} setTransfers={applyTransfers} canEdit={p.addEntry} catMap={catMap} onOpenEntry={p.edit ? editEntryRow : null} units={allowedUnits} activeUnit={activeUnit} defaultUnit={unitDefaultForNew()} canInterUnit={!!p.interUnitTransfer} onInterUnit={doInterUnitTransfer} />
          )}

          {screen === 'overview' && p.cashflow && (
            <div className="screen-enter">
              {p.seeMoney && <ALERTS.AlertBanner alerts={alerts} />}
              {/* ALUR KERJA — accounting workflow map (Record → Journal → Trial balance → Reconcile →
                  Close), each a link with a status dot. Renders only when accounting v2 is live. */}
              {p.reports && ACCT.WorkflowPanel && <ACCT.WorkflowPanel onNav={go} />}
              {/* Langganan jatuh tempo — subscriptions due within 7 days (overdue rows first). Self-hides
                  when the engine is off or nothing is due. */}
              {p.reports && ACCT.SubsDueCard && <ACCT.SubsDueCard onNav={go} />}
              {/* Dashboard Keuangan — KPI cards (each states its period scope + drills to the ledger),
                  cash position per account, P&L month-vs-last, 12-month trend, ratios. All over the
                  cash-book figures the shell already computes; AR/liabilities gated to the engine. */}
              <FIN.Dashboard stats={stats} cashOnHand={cashOnHand} deltas={deltas} ladder={ladder} ladderDeltas={ladderDeltas} shownAccounts={scopedAccounts} allAccounts={accounts} allEntries={entries} transfers={transfers} plEntries={scopedEntries} breakdown={breakdown} periodLbl={periodLbl} seeMoney={p.seeMoney} onDrill={drillTo}
                onOpenEntry={p.edit ? editEntryRow : null}
                onReload={p.settings ? () => { reloadEntries(); reloadAccounts(); } : null} />
              {/* Per-unit breakdown — only in the combined view. Its columns sum EXACTLY to the
                  StatRow above (the Stage-3 invariant). Clicking a unit focuses the whole view. */}
              {p.seeMoney && activeUnit === 'all' && unitStats.length > 1 && (
                <div className="card unit-breakdown">
                  <div className="ub-head"><IconStore s={15} />{tr('unit.byUnit')}</div>
                  <div className="ub-rows">
                    {unitStats.map((u) => (u.elim ? (
                      <div key={u.id} className="ub-row ub-elim" title={tr('unit.internalHint')}>
                        <span className="ub-name"><IconRefresh s={12} /> {u.name}</span>
                        <span className="ub-cell tnum amt-neg">{FIN.fmt(u.t.income)}</span>
                        <span className="ub-cell tnum amt-pos">+{FIN.fmt(-u.t.expense)}</span>
                        <span className="ub-cell tnum">—</span>
                      </div>
                    ) : (
                      <button key={u.id} type="button" className="ub-row" onClick={() => setActiveUnit(u.id)}>
                        <span className="ub-name">{u.name}</span>
                        <span className="ub-cell tnum amt-pos">+{FIN.fmt(u.t.income)}</span>
                        <span className="ub-cell tnum amt-neg">−{FIN.fmt(u.t.expense)}</span>
                        <span className="ub-cell tnum ub-bal">{FIN.fmt(u.t.balance)}</span>
                      </button>
                    )))}
                    <div className="ub-row ub-total">
                      <span className="ub-name">{tr('unit.all')}</span>
                      <span className="ub-cell tnum amt-pos">+{FIN.fmt(stats.income)}</span>
                      <span className="ub-cell tnum amt-neg">−{FIN.fmt(stats.expense)}</span>
                      <span className="ub-cell tnum ub-bal">{FIN.fmt(stats.balance)}</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="fin-grid">
                <div className="fin-col">
                  {/* Ringkasan is READ-ONLY: entry happens in Transaksi. This primary button jumps there
                      with the form open (period + unit carry over via global state). */}
                  {p.addEntry && (
                    <button type="button" className="btn btn-primary catat-btn" onClick={() => catatTransaksi('expense')}>
                      <IconPlus s={18} />{tr('add.catat')}
                    </button>
                  )}
                  {/* Catatan Terbaru — read-only; each row links into Transaksi (no inline edit/delete). */}
                  <FIN.EntriesList entries={recent} title={tr('recent.title')} catMap={catMap} canDelete={false} canEdit={false} onRowClick={() => go('entries')} />
                </div>
                <div className="fin-col">
                  {/* The 12-month trend + expense donut now live in the dashboard above; keep the
                      at-a-glance "today" card beside the recent-entries list. */}
                  <FIN.TodayCard today={today} seeMoney={p.seeMoney} />
                </div>
              </div>
            </div>
          )}

          {/* TRANSAKSI folds Kas & Bank in as a tab (not a separate nav item): Catatan · Kas & Bank. */}
          {(screen === 'entries' || screen === 'moneyspots') && (p.allEntries || p.cashflow) && (
            <div className="lap-tabs no-print">
              {p.allEntries && <button type="button" className={`lap-tab ${screen === 'entries' ? 'on' : ''}`} onClick={() => go('entries')}>{tr('entries.tabRecords')}</button>}
              {p.cashflow && <button type="button" className={`lap-tab ${screen === 'moneyspots' ? 'on' : ''}`} onClick={() => go('moneyspots')}>{tr('nav.moneyspots')}</button>}
            </div>
          )}
          {screen === 'entries' && p.allEntries && (() => {
            // Split for DISPLAY only. Both sides are the same Entry data, post the same journals and
            // feed the same reports/balances; the combined line below always shows the real position.
            const tabRows = txnTab === 'setoran' ? setoranPeriod : txnTab === 'produksi' ? produksiPeriod : txnTab === 'operasional' ? operasionalPeriod : periodEntries;
            // Per-tab and combined both come from the SHARED reducer with the SAME scope as Ringkasan,
            // so the combined figure is byte-for-byte the Ringkasan Pemasukan/Pengeluaran (= stats).
            const tabSum = FTOT.periodTotals(tabRows, range.start, range.end, activeUnit === 'all');
            const combined = { income: stats.income, expense: stats.expense };   // == Ringkasan
            const openDist = () => go('setoran');
            return (
              <div className="screen-enter">
                {/* ENTRY POINT: the form lives HERE only. Opened by [Transaksi Baru] or the ?new= deep
                    link; it creates OPERASIONAL entries, and the new row shows in the list below at once. */}
                <div className="txn-toolbar no-print">
                  <div className="txn-tabs seg" role="tablist">
                    {[['semua', tr('txn.tabAll')], ['setoran', tr('txn.tabSetoran')], ['produksi', tr('txn.tabProduksi')], ['operasional', tr('txn.tabOps')]].map(([k, lbl]) => (
                      <button key={k} type="button" role="tab" aria-selected={txnTab === k} className={`seg-btn ${txnTab === k ? 'on' : ''}`} onClick={() => changeTxnTab(k)}>{lbl}</button>
                    ))}
                  </div>
                  {p.addEntry && !txnFormOpen && (
                    <button type="button" className="btn btn-primary txn-new-btn" onClick={() => setTxnForm(true, 'expense')}><IconPlus s={17} />{tr('txn.new')}</button>
                  )}
                </div>
                {p.addEntry && txnFormOpen && (
                  <div style={{ marginBottom: 16 }}>
                    <FIN.AddEntry key={txnNewType} onAdd={add} defaultType={txnNewType} onClose={() => setTxnForm(false)} incomeCats={cats.income} expenseCats={cats.expense} accounts={accounts} units={allowedUnits} defaultUnit={unitDefaultForNew()} />
                  </div>
                )}
                <div className="txn-splitnote">{tr('txn.splitNote')}</div>
                {p.seeMoney && <FIN.TxnSummary tab={txnTab} period={periodLbl} income={tabSum.income} expense={tabSum.expense} combinedIncome={combined.income} combinedExpense={combined.expense} />}
                <div style={{ marginTop: 16 }}>
                  {txnTab === 'setoran'
                    ? <FIN.SetoranMirror rows={setoranRowsPeriod} onOpenDist={openDist} />
                    : txnTab === 'produksi'
                      ? <FIN.OperasionalTable entries={produksiPeriod} accounts={accounts} catMap={catMap} canEdit={p.edit} canDelete={p.delete} onEdit={editEntryRow} onDelete={del} runningById={opsRunningById} negAccts={openingNeededAccts} onFixOpening={() => go('moneyspots')} />
                      : txnTab === 'operasional'
                        ? <FIN.OperasionalTable entries={operasionalPeriod} accounts={accounts} catMap={catMap} canEdit={p.edit} canDelete={p.delete} onEdit={editEntryRow} onDelete={del} runningById={opsRunningById} negAccts={openingNeededAccts} onFixOpening={() => go('moneyspots')} />
                        : <FIN.EntriesList entries={periodEntries} onDelete={del} onEdit={editEntryRow} filterable showSource title={tr('entries.titleMonth', { m: periodLbl })} catMap={catMap} canDelete={p.delete} canEdit={p.edit} />}
                </div>
              </div>
            );
          })()}

          {screen === 'projects' && p.company && p.reset && (
            <COMPANY.ProjectsScreen projects={projects} setProjects={applyProjects} canEdit={true} />
          )}

          {screen === 'company' && p.company && (            <COMPANY.CompanyDashboard fin={stats} staff={scopedStaff} rates={hrdRates} budget={hrBudget} approvals={approvals} setApprovals={applyApprovals} role={user.role} projects={projects} setoran={setoran} onApproveLeave={applyLeaveToAtt} onApproveDeduction={approveDeduction} onSubmitRequest={submitRequest} onCancelRequest={cancelRequest} onDeleteRequest={deleteRequest} userName={user.name} />
          )}

          {screen === 'headcount' && p.payroll && p.attendance && (
            <COMPANY.HeadcountAffordability staff={scopedStaff} rates={hrdRates} budget={hrBudget} setBudget={applyBudget} canEdit={p.payroll} />
          )}

          {screen === 'hrreport' && p.employees && (
            <COMPANY.HRReport staff={scopedStaff} rates={hrdRates} departments={departments} budget={hrBudget} monthKey={monthKey} today={FIN.TODAY} approvals={approvals} gran={gran} anchor={anchor} setAnchor={setAnchor} range={range} periodLbl={periodLbl} setGran={setGran} />
          )}

          {screen === 'hrsettings' && p.payroll && p.attendance && (            <PAYROLL.HrSettings rates={hrdRates} setRates={applyRates} departments={departments} setDepartments={applyDepartments} positions={positions} setPositions={applyPositions} staff={hrdStaff} setStaff={applyStaff} canEditDept={p.payroll} />
          )}

          {screen === 'employees' && p.employees && (
            <COMPANY.EmployeeDirectory staff={scopedStaff} rates={hrdRates} departments={departments} positions={positions} setPositions={applyPositions} monthKey={monthKey} today={FIN.TODAY} onOpen={openEmp} onEdit={() => navigate('payroll')} canEdit={p.employees} seeMoney={p.seeMoney} setStaff={applyStaff} />
          )}

          {screen === 'hrcalendar' && p.employees && (
            <COMPANY.HrCalendar staff={hrdStaff} rates={hrdRates} events={calEvents} setEvents={applyEvents} today={FIN.TODAY} canEdit={p.attendance || p.payroll} />
          )}

          {screen === 'orientation' && p.payroll && (
            <COMPANY.OrientationScreen staff={hrdStaff} setStaff={applyStaff} rates={hrdRates} today={FIN.TODAY} syncTick={syncTick} canEdit={p.employees} canAddEntry={p.addEntry} onGraduate={graduateOrientation} onFail={failOrientation} onPay={payOrientation} orientationPaidIds={orientationPaidIds} onOpen={openEmp} />
          )}

          {screen === 'kasbon' && p.kasbonView && (
            <COMPANY.KasbonScreen staff={hrdStaff} cashbons={cashbons} onAddCashbon={onAddCashbon} onDecideCashbon={onDecideCashbon} onRemoveCashbon={onRemoveCashbon} canRequest={p.kasbonRequest} canApprove={p.kasbonApprove} canReject={p.kasbonReject} canCancelCap={p.kasbonCancel} canDeleteCap={p.kasbonDelete} currentUserId={user.id} today={FIN.TODAY} userName={user.name} />
          )}

          {screen === 'approvals' && (p.approvals || p.distribusiApprove || p.distribusiGantiRugiHarga) && (
            <div className="screen-enter">
              {p.approvals && <COMPANY.ApprovalsCard approvals={approvals} setApprovals={applyApprovals} role={user.role} canSubmit={p.approvals} staff={hrdStaff} onApproveLeave={applyLeaveToAtt} onApproveDeduction={approveDeduction} onSubmitRequest={submitRequest} onCancelRequest={cancelRequest} onDeleteRequest={deleteRequest} userName={user.name} />}
              {/* Distribusi correction/void approval inbox — same screen, its own capability. */}
              {(p.distribusiApprove || p.distribusiGantiRugiHarga) && <DIST.ChangeRequests refreshKey={distTick} fleetScope={user && user.fleetScope} fleet={fleet} distFleet={distFleet} setDistFleet={setDistFleet} onChanged={() => setDistTick((t) => t + 1)} currentUserId={user && user.id} canApproveSelf={!!p.distribusiApproveSelf} selfApproveLimit={+p.maxSelfApproveAmount || 0} />}
            </div>
          )}

          {/* LAPORAN hub — Neraca · Laba Rugi · Jurnal · Buku Besar · Arus Kas as tabs in one viewer.
              Each tab is the EXISTING screen unchanged (same server calls → identical figures); the hub
              only adds the tab bar. Old deep-links (#acct-neraca, #acct-labarugi, #ledger) open the
              matching tab via the seeding effect above. */}
          {(screen === 'reports' || screen === 'ledger' || screen === 'acct-neraca' || screen === 'acct-labarugi') && p.reports && (<>
            <div className="lap-tabs no-print">
              {[['neraca', tr('nav.finBS')], ['labarugi', tr('nav.finIS')], ['jurnal', tr('nav.finJournal')], ['bukubesar', tr('nav.finLedger')], ['aruskas', tr('nav.finCashflow')]].map(([k, l]) => (
                <button key={k} type="button" className={`lap-tab ${laporanTab === k ? 'on' : ''}`} onClick={() => setLaporanTab(k)}>{l}</button>
              ))}
            </div>
            {laporanTab === 'neraca' && ACCT.BalanceSheetScreen && <ACCT.BalanceSheetScreen unitLabel={activeUnitName} />}
            {laporanTab === 'labarugi' && ACCT.IncomeStatementScreen && <ACCT.IncomeStatementScreen businessUnitId={activeUnit === 'all' ? undefined : activeUnit} fleetId={distFleet && distFleet !== 'all' ? distFleet : undefined} unitLabel={activeUnitName} />}
            {laporanTab === 'jurnal' && ACCT.JournalScreen && <ACCT.JournalScreen businessUnitId={activeUnit === 'all' ? undefined : activeUnit} fleetId={distFleet && distFleet !== 'all' ? distFleet : undefined} unitLabel={activeUnitName} onOpenEntry={p.edit ? editEntryRow : null} />}
            {laporanTab === 'bukubesar' && <ACCT.LedgerScreen businessUnitId={activeUnit === 'all' ? undefined : activeUnit} fleetId={distFleet && distFleet !== 'all' ? distFleet : undefined} unitLabel={activeUnitName} onOpenEntry={() => go('entries')} />}
            {laporanTab === 'aruskas' && <REPORTS.ReportsScreen entries={reportEntries} catMap={catMap} userName={user.name} rates={hrdRates} staff={scopedStaff} payrollPosted={payrollPosted} payrollTotal={hrdTotals.companyCost} payrollLabel={curPayLabel} onPostPayroll={() => postPayroll(hrdTotals.companyCost, curPayLabel)} onOpenEntry={p.edit ? editEntryRow : null} unitLabel={activeUnit === 'all' ? null : (businessUnits.find((u) => u.id === activeUnit) || {}).name} businessUnitId={activeUnit === 'all' ? undefined : activeUnit} />}
          </>)}

          {screen === 'thr' && p.payroll && (
            <COMPANY.ThrScreen staff={scopedStaff} rates={hrdRates} setRates={applyRates} today={FIN.TODAY} posted={thrPosted} onPost={postThr} canPost={p.addEntry || p.payroll} canEdit={p.payroll} />
          )}

          {/* ACCOUNTING v2 workflow screens (double-entry). Each fetches from the flag-gated /accounting
              API and shows the informative "coming soon" card itself when the flag is off (404). */}
          {/* Buku Besar / Neraca / Laba Rugi are now TABS inside the Laporan hub (above). Neraca Saldo
              stays its own screen under "Pengaturan Akuntansi" as a verification check. */}
          {screen === 'acct-trialbalance' && p.reports && ACCT.TrialBalanceScreen && (<ACCT.TrialBalanceScreen businessUnitId={activeUnit === 'all' ? undefined : activeUnit} fleetId={distFleet && distFleet !== 'all' ? distFleet : undefined} unitLabel={activeUnitName} />)}
          {screen === 'reconcile' && p.reports && (<ACCT.ReconcileScreen accounts={accounts} />)}
          {screen === 'acct-mapping' && p.reports && (<ACCT.MappingScreen canEdit={user.role === 'owner' || user.role === 'gm'} />)}
          {screen === 'acct-backfill' && p.reports && (user.role === 'owner' || user.role === 'gm') && (<ACCT.BackfillScreen canRun={user.role === 'owner' || user.role === 'gm'} />)}
          {screen === 'acct-payables' && p.reports && ACCT.PayablesScreen && (<ACCT.PayablesScreen canRun={!!p.reports} accounts={accounts} />)}
          {/* BIAYA TETAP hub — "recurring costs that post themselves": Langganan · Aset & Penyusutan ·
              Akrual & Prabayar as tabs. Each tab is the EXISTING screen unchanged. Old deep-links open the
              matching tab. */}
          {(screen === 'biaya-tetap' || screen === 'acct-subscriptions' || screen === 'acct-accrual' || screen === 'acct-assets') && p.reports && (<>
            <div className="lap-tabs no-print">
              {[['langganan', tr('nav.finSubs')], ['aset', tr('nav.finAssets')], ['akrual', tr('nav.finAccrual')]].map(([k, l]) => (
                <button key={k} type="button" className={`lap-tab ${biayaTab === k ? 'on' : ''}`} onClick={() => setBiayaTab(k)}>{l}</button>
              ))}
            </div>
            {biayaTab === 'langganan' && ACCT.SubscriptionScreen && <ACCT.SubscriptionScreen canRun={!!p.reports} />}
            {biayaTab === 'aset' && ACCT.AssetsScreen && <ACCT.AssetsScreen canRun={user && (user.role === 'owner' || user.role === 'gm')} isOwner={!!(user && user.role === 'owner')} />}
            {biayaTab === 'akrual' && ACCT.AccrualScreen && <ACCT.AccrualScreen canRun={!!p.reports} />}
          </>)}
          {screen === 'acct-costing' && p.reports && ACCT.CostingScreen && (<ACCT.CostingScreen canRun={user && (user.role === 'owner' || user.role === 'gm')} />)}
          {screen === 'acct-payroll' && p.sdmPayrollLihat && ACCT.PayrollAccrualScreen && (<ACCT.PayrollAccrualScreen canRun={!!p.sdmPayrollKelola} canApprove={!!p.sdmPayrollSetujui} />)}
          {screen === 'close' && p.reports && (<ACCT.CloseScreen isOwner={user.role === 'owner'} onNav={go} />)}

          {screen === 'payroll' && p.payroll && (
            <PAYROLL.PayrollScreen defaultUnit={unitDefaultForNew()} rates={hrdRates} setRates={applyRates} staff={scopedStaff} setStaff={applyStaff} monLabel={curPayLabel} onPost={postPayroll} canEdit={p.employees} cashbons={cashbons} monthKey={monthKey} departments={departments} positions={positions} setPositions={applyPositions} />
          )}

          {screen === 'settings' && p.settings && (
            <SETTINGS.SettingsScreen cats={cats} onChange={applyCats} canWipe={!!p.dataWipe} canManageUnits={!!p.manageBusinessUnits} canDocDesign={!!p.distribusiDokumenDesain} canDocSign={!!p.distribusiDokumenTtd} settings={settings} onSettingsChange={applySettings} entries={entries} accounts={accounts} catLabel={(k) => FS.catInfo(catMap, k).label} />
          )}

          {screen === 'users' && p.manageUsers && (
            <USERMGMT.UserManagement users={users} setUsers={setUsers} currentId={user.id} roles={roles} onRolesChanged={reloadRoles} canManageRoles={!!p.manageUsers} fleet={fleet} businessUnits={businessUnits} ownerAdmin={user && user.role === 'owner'} />
          )}

          <footer className="app-footer">
            <span>© 2026 AirRO Reverse Osmosis · {tr('nav.cashbook')} · {user.name}</span>
            {/* Build stamp — which bundle is live at a glance (commit + build time, embedded by build.mjs). */}
            <BuildStamp />
          </footer>
        </div>
      </main>

      {hpDock && window.DHP ? <window.DHP.Dock navIds={NAV.map((n) => n.id)} perms={p} screen={screen} onGo={go} onPick={(k) => hp.onEntry(k)} /> : null}
      {hpEntry && window.DHP && window.DHP.Entry ? <window.DHP.Entry {...hpEntry} perms={p} rules={fieldRules} today={FIN.TODAY} userId={user && user.id}
        staffMode={!!(p.distribusi && !p.distribusiHargaMaster && !p.distribusiAudit && !p.distribusiCustomers)}
        fleet={fleet} fleetScope={user && user.fleetScope} distFleet={distFleet} onDone={() => setDistTick((t) => t + 1)} onClose={dismissOverlay} /> : null}
      <nav className="mobile-nav">
        {NAV.filter((n) => !n.hidden).slice(0, 4).map((n) => (
          <button key={n.id} className={`mnav ${screen === n.id ? 'on' : ''}`} onClick={() => go(n.id)}>{Ish(n.icon, { s: 22 })}<span>{n.label}</span></button>
        ))}
        {/* "Menu" opens the drawer with EVERY permitted menu (finance + HR + admin),
            so nothing is unreachable on mobile. Highlighted when the drawer is open
            OR the current screen isn't one of the 4 quick items. Logout lives in the
            drawer's user chip. */}
        <button className={`mnav ${drawer || !NAV.filter((n) => !n.hidden).slice(0, 4).some((n) => n.id === screen) ? 'on' : ''}`} onClick={openDrawer}><IconMenu s={22} /><span>{tr('nav.more')}</span></button>
      </nav>

      {globalOverlays}
      {editing && p.edit && (
        <EDIT.EntryModal entry={editing} incomeCats={cats.income} expenseCats={cats.expense} accounts={accounts} units={allowedUnits} onSave={saveEdit} onClose={dismissOverlay} />
      )}
      {empDetail && p.empDetail && (
        <COMPANY.EmployeeDetail staff={empDetail} rates={hrdRates} departments={departments} positions={positions} setPositions={applyPositions} monthKey={monthKey} today={FIN.TODAY} syncTick={syncTick} seeMoney={p.seeMoney} canEdit={p.employees} canEditAtt={p.attendance && p.payroll} onSyncDeduct={syncLateDeduct} onEdit={() => navigateFromOverlay('payroll')} onClose={dismissOverlay} onSaveStaff={upsertStaff} cashbons={cashbons} onAddCashbon={onAddCashbon} onUpdateCashbon={onUpdateCashbon} onDecideCashbon={onDecideCashbon} onRemoveCashbon={onRemoveCashbon} canApprove={p.kasbonApprove} canReject={p.kasbonReject} canCancelCap={p.kasbonCancel} canDeleteCap={p.kasbonDelete} currentUserId={user.id} onGraduate={graduateOrientation} onFailOrientation={failOrientation} onPayOrientation={payOrientation} orientationPaid={orientationPaidIds.includes(empDetail.id)} canAddEntry={p.addEntry} />
      )}
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<FApp />);
